import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ChevronDown,
	ChevronRight,
	FileCode,
	FileDown,
	Loader2,
	RefreshCw,
	Stethoscope,
} from "lucide-react";
import { Fragment, type ReactNode, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	ConfirmDialog,
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NoticeBanner } from "@/components/ui/notice-banner";
import { PageNote } from "@/components/ui/page-legend";
import { useAuth } from "@/features/auth/use-auth";
import { CheckPharmacyPanel } from "@/features/outbound/check-pharmacy-panel";
import { DGAKT_STALE_DAYS, dgaktLabel, isDgaktStale } from "@/features/outbound/dgakt-age";
import { DokfLinesPanel } from "@/features/outbound/dokf-lines-panel";
import { invisibleHint } from "@/features/outbound/invisible-diff";
import { InvisibleFlag } from "@/features/outbound/invisible-flag";
import {
	type AggregateDiffRow,
	outboundApi,
	type SfyApplyScope,
	type SfyBlozChange,
	type SfyBucketDetail,
	type SfyFlaggedFirm,
	type SfyOverviewRow,
} from "@/features/outbound/outbound-api";
import { SfyBatchSendDialog } from "@/features/outbound/sfy-batch-send-dialog";
import {
	cellState,
	contractBlozCounts,
	freshOverviewRow,
	groupOverview,
	lastCompletedCronStartUtc,
	type SfyCellTone,
	type SfyMeasureTarget,
	type SfyOverviewMatrixRow,
	type SfyProducerGroup,
	SNAPSHOT_STALE_HOURS,
	snapshotAgeHours,
} from "@/features/outbound/sfy-overview";
import { templateDrift } from "@/features/outbound/sfy-query-templates";
import { resolveSfyConnectionId, sfySearchSchema } from "@/features/outbound/sfy-search";
import { sfySendScopeLabels } from "@/features/outbound/sfy-send-scope";
import { type ExcelColumn, exportToExcel } from "@/lib/export-excel";
import { ensureElementVisible } from "@/lib/route-guards";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_auth/konfiguracja/sfy_/przeglad")({
	validateSearch: (search) => sfySearchSchema.parse(search),
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(context.queryClient, "sidebar.system.sfy-przeglad", location.href);
	},
	component: SfyOverviewPage,
});

const TONE_CLASS: Record<SfyCellTone, string> = {
	success: "border-status-success-border bg-status-success-bg text-status-success-text",
	warning: "border-status-warning-border bg-status-warning-bg text-status-warning-text",
	error: "border-status-error-border bg-status-error-bg text-status-error-text",
	neutral: "border-border bg-muted/40 text-muted-foreground",
};

interface CellTarget {
	producerName: string;
	row: SfyOverviewMatrixRow;
	overview: SfyOverviewRow;
}

/** Czas trwania w czytelnej formie: "820 ms", "12,3 s", "2 min 5 s". */
function fmtDuration(ms: number): string {
	if (ms < 1000) return `${ms} ms`;
	const s = ms / 1000;
	if (s < 60) return `${s.toFixed(1).replace(".", ",")} s`;
	return `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
}

/** Krotki absolutny czas snapshotu, np. "22.07, 04:12". */
/**
 * Liczba z klucza naturalnego, po nazwie kolumny bez wzgledu na wielkosc liter
 * (klienci maja IDFIRM, idfirm, Id). `null` gdy klucza nie ma albo nie jest
 * liczba — wtedy nie ma czego otwierac i wiersz zostaje nieklikalny.
 */
function numerZKlucza(matchKey: Record<string, string>, nazwa: string): number | null {
	const wpis = Object.entries(matchKey).find(([k]) => k.toLowerCase() === nazwa.toLowerCase());
	if (!wpis) return null;
	const n = Number(wpis[1]);
	return Number.isFinite(n) ? n : null;
}

/**
 * Kto dostaje nocne podsumowanie. Widoczne w naglowku, a nie schowane w
 * konfiguracji: adresat, o ktorym nikt nie wie, jest gorszy od jego braku —
 * ludzie zakladaja, ze „ktos to dostaje", i nikt nie sprawdza.
 *
 * Pusta lista = mail wylaczony i tak jest napisane wprost.
 */
function SummaryRecipients() {
	const qc = useQueryClient();
	const [edycja, setEdycja] = useState(false);
	const [tekst, setTekst] = useState("");
	const [blad, setBlad] = useState<string | null>(null);

	const q = useQuery({
		queryKey: ["sfy", "summary-recipients"],
		queryFn: () => outboundApi.getSfySummaryRecipients(),
		staleTime: 5 * 60 * 1000,
	});
	const zapisz = useMutation({
		mutationFn: () =>
			outboundApi.setSfySummaryRecipients(
				tekst
					.split(/[,;\s]+/)
					.map((a) => a.trim())
					.filter(Boolean),
			),
		onSuccess: (v) => {
			qc.setQueryData(["sfy", "summary-recipients"], v);
			setEdycja(false);
			setBlad(null);
		},
		onError: (e: unknown) => setBlad(e instanceof Error ? e.message : "Blad zapisu adresow"),
	});

	const lista = q.data ?? [];

	if (edycja) {
		return (
			<div className="mt-1 space-y-1">
				<div className="flex items-center gap-2 max-md:flex-wrap">
					<Input
						value={tekst}
						onChange={(e) => setTekst(e.target.value)}
						placeholder="adres@firma.pl, drugi@firma.pl"
						className="h-7 max-w-md text-xs max-md:h-9 max-md:basis-full"
					/>
					<Button
						size="sm"
						onClick={() => zapisz.mutate()}
						disabled={zapisz.isPending}
						className="max-md:h-9"
					>
						{zapisz.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
						Zapisz
					</Button>
					<Button size="sm" variant="ghost" onClick={() => setEdycja(false)} className="max-md:h-9">
						Anuluj
					</Button>
				</div>
				{blad && <p className="text-xs text-status-error-text">{blad}</p>}
			</div>
		);
	}

	return (
		<p className="text-xs text-muted-foreground max-md:wrap-anywhere">
			Podsumowanie po nocnej wysylce idzie na:{" "}
			{lista.length === 0 ? (
				<span className="text-status-warning-text">
					nikogo — mail wylaczony, dopoki nie wpiszesz adresu
				</span>
			) : (
				<strong>{lista.join(", ")}</strong>
			)}{" "}
			<button
				type="button"
				className="underline hover:no-underline max-md:inline-block max-md:py-2"
				onClick={() => {
					setTekst(lista.join(", "));
					setEdycja(true);
				}}
			>
				zmien
			</button>
		</p>
	);
}

/**
 * Linia „braki od <data>" / „roznice od <data>" pod licznikiem.
 *
 * Nie renderuje sie w trybie agregatu ani przy zerze — backend daje wtedy NULL
 * (w agregacie `source_values` to sumy kubelka, kolumny dgakt tam nie ma).
 *
 * Powyzej progu swieci na ostrzegawczo: rozbieznosc sprzed tygodnia to co innego
 * niz wczorajszy poslizg, a sama data tego nie krzyczy.
 */
function DgaktAgeLine({
	prefix,
	iso,
	co,
}: {
	prefix: string;
	iso?: string | null;
	/** Dopelniacz do tooltipa: „brakujacych" / „rozniacych sie". */
	co: string;
}) {
	if (!iso) return null;
	const stale = isDgaktStale(iso, new Date());
	return (
		<span
			className={cn(
				"text-[10px] whitespace-nowrap",
				stale ? "font-medium text-status-warning-text" : "text-muted-foreground",
			)}
			title={`Najstarszy dgakt wsrod wierszy ${co} (${iso})${
				stale ? ` — starszy niz ${DGAKT_STALE_DAYS} dni` : ""
			}. Sam licznik tego nie mowi.`}
		>
			{stale && "⚠ "}
			{dgaktLabel(prefix, iso, new Date())}
		</span>
	);
}

function fmtSnapshotTime(iso: string): string {
	return new Date(iso).toLocaleString("pl-PL", {
		day: "2-digit",
		month: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function SfyOverviewPage() {
	const { user } = useAuth();
	const isSuperAdmin = !!user?.roles?.includes("super_admin");
	const [target, setTarget] = useState<CellTarget | null>(null);

	const qc = useQueryClient();
	const overviewQuery = useQuery({
		queryKey: ["sfy", "overview"],
		queryFn: () => outboundApi.getSfyOverview(),
		enabled: isSuperAdmin,
		// Przeglad ma pokazywac AKTUALNY stan snapshotow (uzytkownik przelicza je
		// recznie na innej stronie). Globalny staleTime 30min trzymalby stary wynik
		// — tu wymuszamy odczyt przy kazdym wejsciu (zapytanie to tani GROUP BY).
		staleTime: 0,
		refetchOnMount: "always",
	});
	// To samo ostrzezenie co w nocnym mailu. Pinowanie dzieje sie na innej stronie,
	// wiec jak przeglad — swiezy odczyt przy kazdym wejsciu (male tabele).
	const unpinnedQuery = useQuery({
		queryKey: ["sfy", "unpinned-current"],
		queryFn: () => outboundApi.getSfyUnpinnedCurrentContracts(),
		enabled: isSuperAdmin,
		staleTime: 0,
		refetchOnMount: "always",
	});
	const unpinned = unpinnedQuery.data ?? [];

	const now = Date.now();
	const groups = useMemo(() => groupOverview(overviewQuery.data ?? []), [overviewQuery.data]);

	// „Sprawdz apteke" dziala na POLACZENIU — na przegladzie moze byc kilku
	// producentow, wiec pozwalamy wybrac, ktore sprawdzamy. Lista z overview, bo
	// tylko te polaczenia maja tu w ogole cokolwiek do pokazania.
	const connections = useMemo(() => {
		const map = new Map<number, string>();
		for (const g of groups) {
			const connId = g.rows[0]?.connectionId;
			if (connId != null && !map.has(connId)) map.set(connId, g.producerName);
		}
		return [...map].map(([id, name]) => ({ id, name }));
	}, [groups]);
	// Wybor w URL (?polaczenie=), wspolny z konfiguracja — przejscie w obie strony
	// zostaje przy tym samym producencie.
	const { polaczenie } = Route.useSearch();
	const navigate = Route.useNavigate();
	const setCheckConnId = (id: number) =>
		navigate({ search: (prev) => ({ ...prev, polaczenie: id }), replace: true });
	const activeCheckConn = resolveSfyConnectionId(polaczenie, connections);
	// Zwijanie kart producentow — przy kilkunastu producentach przeglad robi sie
	// jednym dlugim scrollem. Domyslnie ZWINIETE: naglowek karty i tak niesie
	// podsumowanie, wiec caly stan widac bez rozwijania czegokolwiek.
	const [expanded, setExpanded] = useState<Set<string>>(new Set());
	const allKeys = useMemo(
		() => groups.map((g) => String(g.producerId ?? g.producerName)),
		[groups],
	);
	const allExpanded = allKeys.length > 0 && allKeys.every((k) => expanded.has(k));

	// Snapshoty starsze niz start ostatniego zakonczonego okna crona (21:35 UTC) —
	// cron ich nie odswiezyl, wiec liczby (w tym istotne/dgakt) moga byc nieaktualne.
	const lastCron = lastCompletedCronStartUtc(now);
	const cronStartLocal = new Date(lastCron).toLocaleTimeString("pl-PL", {
		hour: "2-digit",
		minute: "2-digit",
	});
	const preCronCount = (overviewQuery.data ?? []).filter(
		(r) => r.snapshotMode !== "off" && r.computedAt && Date.parse(r.computedAt) < lastCron,
	).length;

	if (!isSuperAdmin) {
		return (
			<div className="p-6">
				<p className="text-muted-foreground">Brak dostepu. Wymagana rola super_admin.</p>
			</div>
		);
	}

	return (
		<div className="p-6 space-y-6 max-md:p-3 max-md:space-y-3">
			<div className="flex items-start justify-between max-md:flex-wrap max-md:gap-2">
				<div>
					<h1 className="text-2xl font-semibold max-md:hidden">Przeglad SFY — stan na dzis</h1>
					<PageNote id="about" title="O widoku">
						<p className="text-sm text-muted-foreground">
							Aktualne rozbieznosci per producent × tabelka × aktywny kontrakt. Klik w komorke —
							braki/roznice i przeliczenie.
						</p>
						<p className="text-xs text-muted-foreground">
							Snapshoty przeliczane automatycznie co noc od <strong>{cronStartLocal}</strong> (21:35
							UTC, okno do 23:00 UTC). Data pod komorka = kiedy dana tabelka byla ostatnio
							przeliczona; „Zrob snapshoty" przelicza recznie.
						</p>
					</PageNote>
					<SummaryRecipients />
				</div>
				<div className="flex items-center gap-2 max-md:flex-wrap max-md:[&>*]:h-9">
					<Button
						variant="outline"
						size="sm"
						onClick={() => overviewQuery.refetch()}
						disabled={overviewQuery.isFetching}
					>
						<RefreshCw className={cn("mr-2 h-4 w-4", overviewQuery.isFetching && "animate-spin")} />
						Odswiez
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => setExpanded(allExpanded ? new Set() : new Set(allKeys))}
						disabled={allKeys.length === 0}
					>
						{allExpanded ? (
							<ChevronRight className="mr-2 h-4 w-4" />
						) : (
							<ChevronDown className="mr-2 h-4 w-4" />
						)}
						{allExpanded ? "Zwin wszystko" : "Rozwin wszystko"}
					</Button>
					<Button asChild variant="outline" size="sm">
						<Link to="/konfiguracja/sfy" search={{ polaczenie }}>
							Konfiguracja
						</Link>
					</Button>
				</div>
			</div>

			{connections.length > 0 && (
				<Card className="border-primary/30">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-base">
							<Stethoscope className="h-5 w-5" /> Sprawdz apteke
						</CardTitle>
						<CardDescription>
							Porownanie WSZYSTKICH aktywnych zapytan polaczenia zawezone do jednej apteki (idfirm)
							dla wybranego kontraktu. Na zywo, bez zapisu snapshotu.
						</CardDescription>
						{connections.length > 1 && (
							<div className="space-y-1 pt-2">
								<Label>Producent</Label>
								<select
									value={activeCheckConn ?? ""}
									onChange={(e) => setCheckConnId(Number(e.target.value))}
									className="h-9 w-full max-w-xl rounded-md border border-input bg-background px-2 text-sm"
								>
									{connections.map((c) => (
										<option key={c.id} value={c.id}>
											{c.name}
										</option>
									))}
								</select>
							</div>
						)}
					</CardHeader>
					<CardContent>
						{activeCheckConn != null && (
							<CheckPharmacyPanel key={activeCheckConn} connectionId={activeCheckConn} />
						)}
					</CardContent>
				</Card>
			)}

			{unpinned.length > 0 && (
				<NoticeBanner
					tone="warning"
					title={`${unpinned.length} nowy(ch) biezacy(ch) kontrakt(ow) bez wysylki`}
					description={
						<>
							Polaczenie wysyla dane innego kontraktu tego producenta, a tego nikt nie przypial —
							jego dane nie ida do klienta. Przypnij i wlacz Kontroluj/Wyslij albo przypnij bez
							nich, jesli swiadomie nie wysylamy:{" "}
							{unpinned.map((k, i) => (
								<span key={`${k.connectionId}-${k.contractId}`}>
									{i > 0 && ", "}
									<Link
										to="/konfiguracja/sfy"
										search={{ polaczenie: k.connectionId }}
										className="underline"
									>
										{k.producerName ?? k.connectionName} — {k.contractName} {k.year}/{k.quarter}
									</Link>
								</span>
							))}
						</>
					}
				/>
			)}

			{preCronCount > 0 && (
				<NoticeBanner
					tone="warning"
					title={`${preCronCount} snapshotow starszych niz start ostatniego crona (${fmtSnapshotTime(new Date(lastCron).toISOString())})`}
					description={
						'Nocny cron ich nie odswiezyl — liczby (w tym klasyfikacja istotne/dgakt) moga byc nieaktualne. Przelicz recznie „Zrob snapshoty" u danego producenta.'
					}
				/>
			)}

			{overviewQuery.isLoading ? (
				<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
			) : overviewQuery.isError ? (
				<NoticeBanner
					tone="error"
					title="Blad ladowania"
					description={
						overviewQuery.error instanceof Error ? overviewQuery.error.message : "Nieznany blad"
					}
				/>
			) : groups.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					Brak skonfigurowanych par (aktywne query + przypiety send_active kontrakt).
				</p>
			) : (
				<div className="space-y-6">
					{groups.map((g) => {
						const materialProblems = g.totalMissing + g.totalMaterialDifferent;
						const dgaktOnly = g.totalDifferent - g.totalMaterialDifferent;
						const blozCounts = contractBlozCounts(g);
						const groupKey = String(g.producerId ?? g.producerName);
						const isCollapsed = !expanded.has(groupKey);
						const toggle = () =>
							setExpanded((prev) => {
								const next = new Set(prev);
								if (next.has(groupKey)) next.delete(groupKey);
								else next.add(groupKey);
								return next;
							});
						return (
							<Card key={g.producerId ?? g.producerName}>
								<CardHeader>
									<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
										<button
											type="button"
											onClick={toggle}
											className="flex items-center gap-2 text-left max-md:min-h-9 max-md:min-w-0"
											title={isCollapsed ? "Rozwin" : "Zwin"}
										>
											{isCollapsed ? (
												<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
											) : (
												<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
											)}
											<div>
												<CardTitle className="text-base">{g.producerName}</CardTitle>
												<CardDescription>
													{g.rows.length} tabelek · {g.contracts.length} aktywnych kontraktow
												</CardDescription>
											</div>
										</button>
										<div className="flex items-center gap-2 max-md:flex-wrap">
											{blozCounts.map((c) => (
												<span
													key={c.contractId}
													title={`Ile roznych blozow obejmuje kontrakt ${c.label} (wszystkie koszyki)`}
												>
													<Badge className="border-status-info-border bg-status-info-bg text-status-info-text text-sm">
														{blozCounts.length > 1 && `${c.label}: `}
														<span className="font-semibold tabular-nums">{c.blozCount}</span>
														&nbsp;BLOZ w kontrakcie
													</Badge>
												</span>
											))}
											<ProducerSnapshotButton
												group={g}
												onDone={() => {
													overviewQuery.refetch();
													// Sumy licza sie PRZY snapshocie, wiec po jego przeliczeniu
													// panel musi zejsc z cache — inaczej pokazuje liczby sprzed
													// zmiany i wyglada, jakby przeliczenie nic nie dalo.
													qc.invalidateQueries({ queryKey: ["sfy", "measure-totals"] });
												}}
											/>
											<Badge
												className={cn(
													materialProblems > 0 ? TONE_CLASS.error : TONE_CLASS.success,
													"text-xs",
												)}
											>
												{materialProblems > 0
													? `${g.totalMissing} braki · ${g.totalMaterialDifferent} roznice${dgaktOnly > 0 ? ` (+${dgaktOnly} dgakt)` : ""}`
													: dgaktOnly > 0
														? `OK (${dgaktOnly} tylko dgakt)`
														: "wszystko OK"}
											</Badge>
										</div>
									</div>
								</CardHeader>
								{!isCollapsed && (
									<CardContent>
										{g.measureTargets.map((t) => (
											<MeasureTotalsPanel key={`${t.connectionId}:${t.contractId}`} target={t} />
										))}
										<div className="overflow-x-auto">
											<table className="w-full text-sm">
												<thead>
													<tr className="border-b text-left">
														<th className="p-2 font-medium">Tabelka</th>
														{g.contracts.map((c) => (
															<th key={c.contractId} className="p-2 font-medium whitespace-nowrap">
																{c.label}
															</th>
														))}
													</tr>
												</thead>
												<tbody>
													{g.rows.map((r) => (
														<tr key={r.key} className="border-b last:border-0">
															<td className="p-2 whitespace-nowrap">
																<span className="font-mono">{r.ourTable}</span>
																{r.queryName !== r.ourTable && (
																	<span className="ml-2 text-xs text-muted-foreground">
																		{r.queryName}
																	</span>
																)}
																<SendModeBadge row={r} />
															</td>
															{g.contracts.map((c) => {
																const ov = r.cells[c.contractId];
																if (!ov) {
																	return (
																		<td key={c.contractId} className="p-2 text-muted-foreground">
																			<span className="text-xs">n/d</span>
																		</td>
																	);
																}
																const st = cellState(ov, now);
																const clickable = ov.snapshotMode !== "off";
																return (
																	<td key={c.contractId} className="p-2 align-top">
																		<div className="flex flex-col gap-0.5">
																			<button
																				type="button"
																				disabled={!clickable}
																				title={st.title}
																				onClick={() =>
																					clickable &&
																					setTarget({
																						producerName: g.producerName,
																						row: r,
																						overview: ov,
																					})
																				}
																				className={cn(
																					"inline-flex w-fit items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium max-md:min-h-9",
																					TONE_CLASS[st.tone],
																					clickable
																						? "cursor-pointer hover:brightness-95"
																						: "cursor-default",
																				)}
																			>
																				{st.failed && <span title={st.title}>❌</span>}
																				{st.stale && <span title="Snapshot nieswiezy">⚠</span>}
																				{st.label}
																			</button>
																			{ov.missing > 0 && ov.missingFirms ? (
																				<span
																					className="text-[10px] whitespace-nowrap text-muted-foreground"
																					title="Liczba roznych aptek (idfirm), ktorych dotycza braki"
																				>
																					{ov.missingFirms} idfirm z brakami
																				</span>
																			) : null}
																			<DgaktAgeLine
																				prefix="braki od"
																				iso={ov.missing > 0 ? ov.missingMinDgakt : null}
																				co="brakujacych"
																			/>
																			<DgaktAgeLine
																				prefix="roznice od"
																				iso={ov.different > 0 ? ov.differentMinDgakt : null}
																				co="rozniacych sie"
																			/>
																			{ov.computedAt && (
																				<span
																					className={cn(
																						"text-[10px] whitespace-nowrap",
																						st.stale
																							? "text-status-warning-text"
																							: "text-muted-foreground",
																					)}
																					title={`Ostatni snapshot tej tabelki${st.stale ? " (nieswiezy)" : ""}`}
																				>
																					{fmtSnapshotTime(ov.computedAt)}
																				</span>
																			)}
																			{ov.lastApplyAt && (
																				<span
																					className={cn(
																						"text-[10px] whitespace-nowrap",
																						ov.lastApplyStatus === "ok"
																							? "text-status-success-text"
																							: "text-status-error-text",
																					)}
																					title={
																						ov.lastApplyError ??
																						`Ostatnia wysylka do klienta (status: ${ov.lastApplyStatus ?? "?"})`
																					}
																				>
																					{ov.lastApplyStatus === "blocked" ? (
																						<>
																							⏸ automat wstrzymany ({ov.lastApplyStatements ?? 0})
																						</>
																					) : (
																						<>
																							↑ {ov.lastApplyApplied ?? 0}/
																							{ov.lastApplyStatements ?? 0} ·{" "}
																							{fmtSnapshotTime(ov.lastApplyAt)}
																						</>
																					)}
																				</span>
																			)}
																		</div>
																	</td>
																);
															})}
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</CardContent>
								)}
							</Card>
						);
					})}
				</div>
			)}

			{target && (
				<CellDetailDialog
					target={target}
					onClose={() => setTarget(null)}
					onRecomputed={() => overviewQuery.refetch()}
				/>
			)}
		</div>
	);
}

/** Znacznik trybu wysylki tego zapytania: automat (nocny cron) / recznie (pod
 *  nadzorem) / brak (tylko kontrola). Automat wymaga TAKZE wysylki na kontrakcie —
 *  gdy jej brak, mowimy o tym wprost, bo cron by go pominal. */
function SendModeBadge({ row }: { row: SfyOverviewMatrixRow }) {
	if (!row.pushActive) return null; // tylko kontrola — bez znacznika, zeby nie halasowac
	const contractArmed = Object.values(row.cells).some((c) => c.contractPushActive);
	if (row.autoApply && !contractArmed) {
		return (
			<span
				className="ml-2 rounded border border-status-warning-border bg-status-warning-bg px-1.5 py-0.5 text-[10px] text-status-warning-text"
				title="Zapytanie ma automat, ale kontrakt nie ma wlaczonej wysylki — cron go pominie"
			>
				automat (kontrakt off)
			</span>
		);
	}
	return row.autoApply ? (
		<span
			className="ml-2 rounded border border-status-error-border bg-status-error-bg px-1.5 py-0.5 text-[10px] text-status-error-text"
			title={`Wysylka AUTOMATEM (nocny cron), limit ${row.autoMaxUpserts} upsertow. Rozjazd tylko na dgakt jest pomijany.`}
		>
			automat
		</span>
	) : (
		<span
			className="ml-2 rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground"
			title="Wysylka tylko RECZNA (pod nadzorem) — cron tego nie wysyla"
		>
			recznie
		</span>
	);
}

/** Przycisk „Zrob snapshoty" per producent — sekwencyjnie przelicza wszystkie
 *  komorki (tabelka × kontrakt) z aktywnym snapshotem (off pomijamy). Sekwencyjnie,
 *  bo pojedyncze zapytania bywaja ciezkie (np. korz ~70k) — nie zalewamy bazy/klienta. */
function ProducerSnapshotButton({
	group,
	onDone,
}: {
	group: SfyProducerGroup;
	onDone: () => void;
}) {
	const [running, setRunning] = useState(false);
	const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
	const [failed, setFailed] = useState(0);

	// Komorki do przeliczenia: istnieja i maja snapshotMode != off.
	const targets = useMemo(() => {
		const out: SfyOverviewRow[] = [];
		for (const r of group.rows) {
			for (const c of group.contracts) {
				const ov = r.cells[c.contractId];
				if (ov && ov.snapshotMode !== "off") out.push(ov);
			}
		}
		return out;
	}, [group]);

	async function run() {
		setRunning(true);
		setFailed(0);
		let fail = 0;
		setProgress({ done: 0, total: targets.length });
		for (let i = 0; i < targets.length; i++) {
			const ov = targets[i];
			try {
				await outboundApi.runSfySnapshot(ov.connectionId, ov.queryId, ov.contractId);
			} catch {
				fail++;
			}
			setProgress({ done: i + 1, total: targets.length });
		}
		setFailed(fail);
		setRunning(false);
		onDone();
	}

	if (targets.length === 0) return null;

	return (
		<div className="flex items-center gap-2">
			{failed > 0 && !running && (
				<span className="text-xs text-status-error-text">{failed} bledow</span>
			)}
			<Button
				variant="outline"
				size="sm"
				onClick={() => void run()}
				disabled={running}
				title="Przelicz snapshoty wszystkich zapytan tego producenta (poza off)"
				className="max-md:h-9"
			>
				{running ? (
					<Loader2 className="mr-2 h-4 w-4 animate-spin" />
				) : (
					<RefreshCw className="mr-2 h-4 w-4" />
				)}
				{running && progress
					? `Snapshoty ${progress.done}/${progress.total}`
					: `Zrob snapshoty (${targets.length})`}
			</Button>
		</div>
	);
}

function CellDetailDialog({
	target,
	onClose,
	onRecomputed,
}: {
	target: CellTarget;
	onClose: () => void;
	onRecomputed: () => void;
}) {
	const qc = useQueryClient();
	const { row, overview } = target;
	const [kind, setKind] = useState<DiffKind>(overview.missing > 0 ? "missing" : "different");
	// Historia domyslnie rozwinieta — jest audytem i ma byc widoczna bez klikania.
	// Zwijana na zadanie, bo przy diagnozie kubelka spycha tabele rozbieznosci w dol.
	const [historiaZwinieta, setHistoriaZwinieta] = useState(false);
	const [error, setError] = useState<string | null>(null);
	/** Stan sprzed ostatniej UDANEJ realnej wysylki — do wykrycia wysylki bez efektu.
	 *  Trzymamy ZBIOR KLUCZY, nie liczniki: dane zyja (robot dociaga wiersze w tle),
	 *  wiec licznik potrafi urosnac mimo ze wysylka zadzialala. Porownanie licznikow
	 *  dawalo wtedy falszywy alarm. Przy duzych zbiorach (> CAP) klucze pomijamy i
	 *  schodzimy do heurystyki licznikowej, jawnie zaznaczonej w komunikacie. */
	const pendingApplyRef = useRef<{
		keys: Set<string> | null;
		missing: number;
		different: number;
		applied: number;
		skippedNoBloz: number;
	} | null>(null);
	const [stalledApply, setStalledApply] = useState<{
		applied: number;
		/** Wiersze odsiane z braku bloz07 — najprostsze wytlumaczenie „zostalo tyle samo”. */
		skippedNoBloz: number;
		/** Ile WYSLANYCH wczesniej rozbieznosci nadal jest zgłaszanych (null = nie wiemy). */
		stillFlagged: number | null;
		missing: { before: number; after: number } | null;
		different: { before: number; after: number } | null;
	} | null>(null);

	const diffsQuery = useQuery({
		queryKey: [
			"sfy",
			"overview",
			"diffs",
			row.connectionId,
			row.queryId,
			overview.contractId,
			kind,
		],
		queryFn: () =>
			outboundApi.listSfyAggregateDiffs(row.connectionId, row.queryId, overview.contractId, {
				kind,
				limit: 200,
			}),
	});

	// Swieze podsumowanie (nie z potencjalnie starego overview) — inaczej naglowek
	// pokazywalby "0/0, nie przeliczony" mimo ze tabela ponizej ma wiersze.
	const summaryQuery = useQuery({
		queryKey: ["sfy", "overview", "summary", row.connectionId, row.queryId, overview.contractId],
		queryFn: () =>
			outboundApi.getSfyAggregateDiffsSummary(row.connectionId, row.queryId, overview.contractId),
		staleTime: 0,
	});
	const missing = summaryQuery.data?.missing ?? overview.missing;
	const different = summaryQuery.data?.different ?? overview.different;
	const materialDifferent = summaryQuery.data?.materialDifferent;
	const extra = summaryQuery.data?.extra ?? 0;
	const computedAt = summaryQuery.data?.computedAt ?? overview.computedAt;

	// source_sql z podstawionymi placeholderami (blozy/apteki/daty) — do recznego
	// sprawdzenia na naszej bazie PG. Lekkie (samo podstawienie, bez EXPLAIN).
	const resolvedSql = useMutation({
		mutationFn: () =>
			outboundApi.getSfyResolvedSql(row.connectionId, row.queryId, overview.contractId),
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad pobrania source_sql"),
	});

	function downloadResolvedSql() {
		const sql = resolvedSql.data?.sql ?? "";
		if (!sql) return;
		downloadBlob(
			sql,
			`sfy_source_${row.ourTable}_${overview.contractName}.sql`.replace(/\s+/g, "_"),
		);
	}

	// Podglad = apply (Etap 2): idempotentny upsert detalu (INSERT-if-not-exists + UPDATE,
	// zero DELETE). To DOCELOWY podglad "co dosłać" — dokladnie to wykona job FM2.
	const applyScript = useMutation({
		mutationFn: () =>
			outboundApi.getSfyApplyScript(
				row.connectionId,
				row.queryId,
				overview.contractId,
				selectIdfirm ? Number(selectIdfirm) : undefined,
			),
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad generacji wysylki"),
	});

	// SQL kontrolny — opcjonalnie zawezony do jednej apteki. Przy tysiacach kluczy
	// kontrola calosci jest nieweryfikowalna (lista i tak obcinana), a jedna apteka
	// daje wynik do sprawdzenia recznie.
	const [selectIdfirm, setSelectIdfirm] = useState("");
	const sendScope = sfySendScopeLabels(selectIdfirm);
	// Podpowiedzi z ZALADOWANEJ strony rozbieznosci (limit 200) — nie udajemy pelnej
	// listy aptek kontraktu, bo jej tu nie mamy; to sa te, ktore widac w tabeli nizej.
	const idfirmSuggestions = useMemo(() => {
		const out = new Set<string>();
		for (const d of diffsQuery.data ?? []) {
			for (const [k, v] of Object.entries(d.matchKey)) {
				if (k.toLowerCase() === "idfirm" && v) out.add(String(v));
			}
		}
		return [...out].sort();
	}, [diffsQuery.data]);
	const applySelect = useMutation({
		mutationFn: () =>
			outboundApi.getSfyApplySelect(
				row.connectionId,
				row.queryId,
				overview.contractId,
				selectIdfirm ? Number(selectIdfirm) : undefined,
			),
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad generacji selecta"),
	});

	function downloadApplySelectSql() {
		const sql = applySelect.data?.sql ?? "";
		if (!sql) return;
		downloadBlob(
			sql,
			`sfy_kontrola_${row.ourTable}_${overview.contractName}.sql`.replace(/\s+/g, "_"),
		);
	}

	function downloadApplySql() {
		const sql = applyScript.data?.sql ?? "";
		if (!sql) return;
		downloadBlob(
			sql,
			`sfy_wysylka_${row.ourTable}_${overview.contractName}.sql`.replace(/\s+/g, "_"),
		);
	}

	// Realny apply / dry-run (Etap 3). Bramki po stronie backendu: send_active+push_active
	// oraz aligned. dry_run nie dotyka klienta.
	const [confirmSend, setConfirmSend] = useState(false);
	const applyRunsQuery = useQuery({
		queryKey: ["sfy", "apply-runs", row.connectionId, row.queryId, overview.contractId],
		queryFn: () => outboundApi.getSfyApplyRuns(row.connectionId, row.queryId, overview.contractId),
		staleTime: 0,
	});
	const applyRun = useMutation({
		mutationFn: (dryRun: boolean) =>
			outboundApi.applySfy(
				row.connectionId,
				row.queryId,
				overview.contractId,
				dryRun,
				selectIdfirm ? Number(selectIdfirm) : undefined,
			),
		onSuccess: (res, dryRun) => {
			applyRunsQuery.refetch();
			// Zapamietujemy stan SPRZED realnej wysylki, zeby nastepne przeliczenie
			// mogło sprawdzic, czy cokolwiek dalo. Wysylka konczaca sie "ok N/N", po
			// ktorej liczby nie drgna, oznacza zwykle brakujaca tabele zalezna u
			// klienta (agregat kzak laczy z TOWR i filtruje po t.BLOZ07 — wiersz bez
			// odpowiednika w TOWR pozostaje dla porownania niewidoczny). Bez tego
			// sygnalu mozna wysylac w kolko bez zadnego efektu.
			if (!dryRun && res.status === "ok" && res.appliedCount > 0) {
				pendingApplyRef.current = {
					keys: null,
					missing,
					different,
					applied: res.appliedCount,
					skippedNoBloz: res.skippedNoBloz ?? 0,
				};
				// Klucze zbieramy PO wysylce, ale snapshot jest ten sam co przed nia
				// (przeliczenie nastepuje dopiero na zadanie), wiec opisuja stan sprzed.
				void collectFlaggedKeys(missing + different).then((keys) => {
					if (pendingApplyRef.current) pendingApplyRef.current.keys = keys;
				});
			}
		},
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad wysylki"),
	});

	// Klucze WSZYSTKICH biezacych rozbieznosci (oba rodzaje). Sluza do sprawdzenia,
	// czy po wysylce znikly TE KONKRETNE, a nie tylko czy licznik spadl.
	const FLAGGED_KEYS_CAP = 500;
	async function collectFlaggedKeys(total: number): Promise<Set<string> | null> {
		if (total > FLAGGED_KEYS_CAP) return null;
		const out = new Set<string>();
		for (const k of ["missing", "different"] as const) {
			const rows = await fetchAllDiffs(row.connectionId, row.queryId, overview.contractId, k);
			for (const d of rows) {
				out.add(
					`${k}|${Object.entries(d.matchKey)
						.sort(([a], [b]) => a.localeCompare(b))
						.map(([kk, vv]) => `${kk}=${vv}`)
						.join(";")}`,
				);
			}
		}
		return out;
	}

	// Rozjazd zapisanego SQL-a z szablonem. Diagnoze zaczyna sie WLASNIE TU, wiec sygnal
	// musi byc tutaj, a nie tylko w konfiguracji — inaczej znow szuka sie przyczyny
	// w danych klienta, podczas gdy problemem jest nieprzegenerowane zapytanie.
	const queryDefsQuery = useQuery({
		queryKey: ["sfy", "queries", row.connectionId],
		queryFn: () => outboundApi.listSfyQueries(row.connectionId),
		staleTime: 5 * 60_000,
	});
	const drift = useMemo(() => {
		const def = queryDefsQuery.data?.find((q) => q.id === row.queryId);
		if (!def) return null;
		const d = templateDrift(def);
		return d && (d.sourceSql || d.aggregate) ? d : null;
	}, [queryDefsQuery.data, row.queryId]);

	// Wejscie w kubelek: agregat mowi „ten kubelek sie rozjezdza", to pokazuje KTORY
	// wiersz i KTORA kolumna. Zakres jest maly (apteka × bloz × miesiac), wiec stac
	// nas na pobranie obu stron w calosci — stad widac tez wiersze NADMIAROWE u
	// klienta, ktorych zwykle porownanie (od naszych kluczy w dol) nie zlapie.
	const bucketDetail = useMutation({
		mutationFn: (diffId: number) =>
			outboundApi.getSfyBucketDetail(row.connectionId, row.queryId, overview.contractId, diffId),
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad szczegolow kubelka"),
	});

	// Wysylka PARTIAMI: apteka po aptece, sekwencyjnie. Powod: kzak_stan rozwija sie
	// na dziesiatki tysiecy upsertow, czego nie przepchnie ani automat (limit), ani
	// jedna wysylka. Kazda partia idzie ta sama sciezka co zwykla wysylka — pelne
	// bramki i osobny wpis w audycie z odnotowana apteka. Potwierdzenie, postep,
	// przerwanie i podsumowanie zyja w SfyBatchSendDialog.
	const [confirmBatch, setConfirmBatch] = useState<SfyFlaggedFirm[] | null>(null);

	const prepareBatch = useMutation({
		mutationFn: () =>
			outboundApi.getSfyFlaggedFirms(row.connectionId, row.queryId, overview.contractId),
		onSuccess: (firms) => {
			if (firms.length === 0) {
				setError("Brak aptek z rozbieznosciami — nie ma czego wysylac.");
				return;
			}
			setConfirmBatch(firms);
		},
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad listy aptek"),
	});

	// Rozklad roznic wg wyzerowanych partii — na zadanie, bo przy duzych snapshotach
	// to pelny przebieg po rozbieznosciach. Odpowiada na pytanie "z czego sklada sie
	// ta masa upsertow", gdy automat co noc bije w limit.
	const ilaktZero = useMutation({
		mutationFn: () =>
			outboundApi.getSfyIlaktZero(row.connectionId, row.queryId, overview.contractId),
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad zliczania ilakt"),
	});

	// Przeliczenie uniewaznia WSZYSTKIE wczesniej wygenerowane artefakty: skrypty
	// powstaly z poprzedniego snapshotu, wiec po przeliczeniu pokazywalyby nieaktualne
	// wiersze. Bez tego czyszczenia straznik "tryb row wysyla 1 upsert na rozbieznosc"
	// porownywal SWIEZE liczniki ze STARYM skryptem i falszywie alarmowal.
	// Deklarowane po tamtych mutacjach, zeby moc je tu zresetowac.
	const recompute = useMutation({
		mutationFn: () =>
			outboundApi.runSfySnapshot(row.connectionId, row.queryId, overview.contractId),
		onSuccess: async () => {
			setError(null);
			applyScript.reset();
			applySelect.reset();
			applyRun.reset();
			ilaktZero.reset();
			bucketDetail.reset();
			qc.invalidateQueries({ queryKey: ["sfy", "overview"] });
			qc.invalidateQueries({ queryKey: ["sfy", "measure-totals"] });
			diffsQuery.refetch();
			onRecomputed();
			const fresh = await summaryQuery.refetch();
			const pending = pendingApplyRef.current;
			pendingApplyRef.current = null;
			if (pending && fresh.data) {
				// Gdy mamy klucze sprzed wysylki, decyduje ICH LOS — licznik moze urosnac
				// przez nowe wiersze dociagniete przez robota i nie mowi nic o skutecznosci.
				let stillFlagged: number | null = null;
				if (pending.keys) {
					const now = await collectFlaggedKeys(fresh.data.missing + fresh.data.different);
					if (now) {
						stillFlagged = [...pending.keys].filter((k) => now.has(k)).length;
					}
				}
				const missingStuck = pending.missing > 0 && fresh.data.missing >= pending.missing;
				const differentStuck = pending.different > 0 && fresh.data.different >= pending.different;
				const problem = stillFlagged !== null ? stillFlagged > 0 : missingStuck || differentStuck;
				setStalledApply(
					problem
						? {
								applied: pending.applied,
								skippedNoBloz: pending.skippedNoBloz,
								stillFlagged,
								missing: missingStuck
									? { before: pending.missing, after: fresh.data.missing }
									: null,
								different: differentStuck
									? { before: pending.different, after: fresh.data.different }
									: null,
							}
						: null,
				);
			}

			// Od razu policz, CO realnie poleci do klienta. Wczesniej trzeba bylo kliknac
			// osobno, wiec po przeliczeniu widziales tylko liczbe KUBELKOW — a ta potrafi
			// znaczyc cos zupelnie innego niz liczba wierszy (agregat stanu: 359 kubelkow
			// -> 9852 rozwiniete -> 2494 realne zmiany).
			//
			// Sklejenie jest TYLKO tutaj, w UI. Nocny cron wchodzi w te sama funkcje
			// przeliczajaca; gdyby rozwiniecie wjechalo do niej, cron dostalby druga petle
			// per apteka na wierzchu pierwszej — czyli mnoznik do rzeczy, ktora 2026-07-25
			// wyczerpala max_worker_processes i polozyla pg_cron, autovacuum i pule.
			//
			// Blad generacji NIE jest bledem przeliczenia: snapshot sie udal i liczniki sa
			// swieze, wiec leci jako ostrzezenie obok, a nie jako czerwone "Blad przeliczenia".
			if ((fresh.data?.missing ?? 0) + (fresh.data?.different ?? 0) > 0) {
				try {
					await applyScript.mutateAsync();
				} catch {
					// mutacja ustawila juz swoj komunikat — nie dubluj
				}
			}
		},
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad przeliczenia"),
	});

	async function exportXlsx() {
		const rows = await fetchAllDiffs(row.connectionId, row.queryId, overview.contractId, kind);
		if (rows.length === 0) return;
		const valueKeys = collectValueKeys(rows, kind);
		const keyCols: ExcelColumn[] = Object.keys(rows[0].matchKey).map((k) => ({
			id: `key_${k}`,
			header: k,
			widthPx: 120,
			isNumeric: false,
		}));
		// Kolumna kontrolna z kartoteki — w pliku ma taka sama wartosc diagnostyczna
		// jak na ekranie, bo listy powyzej 200 wierszy oglada sie wlasnie w Excelu.
		const towrCols: ExcelColumn[] = rows.some((r) => r.towrBloz07 != null)
			? [{ id: "towr_bloz07", header: "bloz07 z towr", widthPx: 120, isNumeric: false }]
			: [];
		const valueCols: ExcelColumn[] = valueKeys.flatMap((vk) =>
			kind === "different"
				? [
						{ id: `src_${vk}`, header: `${vk} (u nas)`, widthPx: 120, isNumeric: false },
						{ id: `dst_${vk}`, header: `${vk} (klient)`, widthPx: 120, isNumeric: false },
					]
				: [
						{
							id: `src_${vk}`,
							header: kind === "extra" ? `${vk} (u klienta)` : vk,
							widthPx: 120,
							isNumeric: false,
						},
					],
		);
		const excelRows = rows.map((d) => {
			const rec: Record<string, unknown> = {};
			for (const [k, v] of Object.entries(d.matchKey)) rec[`key_${k}`] = v;
			rec.towr_bloz07 = d.towrBloz07 ?? "";
			for (const vk of valueKeys) {
				rec[`src_${vk}`] = (kind === "extra" ? d.destValues?.[vk] : d.sourceValues?.[vk]) ?? "";
				if (kind === "different") rec[`dst_${vk}`] = d.destValues?.[vk] ?? "";
			}
			return rec;
		});
		await exportToExcel({
			columns: [...keyCols, ...towrCols, ...valueCols],
			rows: excelRows,
			fileName: `sfy_${row.ourTable}_${overview.contractName}_${kind}.xlsx`.replace(/\s+/g, "_"),
		});
	}

	// lastError MUSI isc ze swiezego podsumowania, nie z `overview` — ten jest
	// zapisany przy otwarciu dialogu, wiec po udanym "Przelicz teraz" naglowek
	// dalej straszylby bledem z poprzedniej proby (a baner ponizej juz nie).
	const st = cellState(freshOverviewRow(overview, summaryQuery.data), Date.now());

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-4xl">
				<DialogHeader>
					<DialogTitle>
						{target.producerName} · {row.ourTable} · {overview.contractName}
						<span
							className="ml-2 rounded border border-border px-1.5 py-0.5 font-mono text-[11px] font-normal text-muted-foreground"
							title={
								overview.snapshotMode === "row"
									? "Snapshot wiersz-po-wierszu — 1 rozbieznosc = 1 upsert"
									: overview.snapshotMode === "aggregate"
										? "Snapshot agregatowy — 1 rozbieznosc = KUBELEK, ktory przy wysylce rozwija sie na wiele wierszy detalu"
										: `Tryb snapshotu: ${overview.snapshotMode}`
							}
						>
							{overview.snapshotMode}
						</span>
						<SendModeBadge row={row} />
					</DialogTitle>
					<DialogDescription>
						Braki: {missing} · Roznice: {different}
						{different > 0 && materialDifferent != null && (
							<>
								{" "}
								<span
									className={
										materialDifferent > 0
											? "text-status-error-text font-medium"
											: "text-status-success-text"
									}
								>
									({materialDifferent} istotne · {different - materialDifferent} tylko dgakt)
								</span>
							</>
						)}{" "}
						· {computedAt ? `snapshot: ${st.title}` : "snapshot jeszcze nie przeliczony"}
						{st.stale && " · ⚠ nieswiezy"}
					</DialogDescription>
					<TotalsLine
						ours={summaryQuery.data?.sourceTotals ?? null}
						client={summaryQuery.data?.destTotals ?? null}
					/>
				</DialogHeader>
				<DialogBody className="space-y-3">
					{error && <NoticeBanner tone="error" title="Blad" description={error} />}
					{ilaktZero.data && (
						<div className="rounded-md border border-border px-2 py-1.5 text-xs">
							{ilaktZero.data.notCountable ? (
								<span className="text-muted-foreground">{ilaktZero.data.notCountable}</span>
							) : (
								<>
									<span className="font-medium">{ilaktZero.data.ilaktZeroed}</span> z{" "}
									<span className="font-medium">{ilaktZero.data.differentTotal}</span> roznic to
									wyzerowane partie (nasz <span className="font-mono">ilakt = 0</span>, klient ma
									jeszcze stan).
									<span className="ml-1 text-muted-foreground">
										To NIE jest szum jak dgakt — bez tej wysylki klient widzi towar, ktorego apteka
										nie ma.
									</span>
								</>
							)}
						</div>
					)}

					{drift && (
						<NoticeBanner
							tone="warning"
							title={`Zapisany ${[drift.sourceSql && "source_sql", drift.aggregate && "agregat"].filter(Boolean).join(" i ")} rozni sie od aktualnego szablonu`}
							description="Zapisany SQL to KOPIA — zmiana szablonu go nie dotyka, a source_sql i agregat regeneruje sie osobno, wiec moga rozjechac sie tez miedzy soba. Rozjazd miedzy nimi daje aligned=false i pusty podglad kubelka. Jesli SQL nie byl dopieszczany recznie: w konfiguracji przegeneruj OBA, zapisz i przelicz snapshot."
						/>
					)}

					{stalledApply && (
						<NoticeBanner
							tone="warning"
							title={
								stalledApply.stillFlagged !== null
									? `Wyslano ${stalledApply.applied} upsertow (ok), ale ${stalledApply.stillFlagged} z tych samych rozbieznosci nadal jest zglaszanych`
									: `Wyslano ${stalledApply.applied} upsertow (ok), a liczba rozbieznosci nie zmalala`
							}
							description={
								<span className="space-y-1">
									{stalledApply.skippedNoBloz > 0 && (
										<span className="block">
											<span className="font-medium">
												Najpierw sprawdz to: ta wysylka pominela {stalledApply.skippedNoBloz}{" "}
												wierszy bez bloz07.
											</span>{" "}
											Ich kubelki z definicji zostaja zgloszone, bo swiadomie ich nie wysylamy —
											wiersz bez identyfikacji produktu nic producentowi nie mowi. Zajrzyj w nie w
											tabeli ponizej i popraw zrodlo w FM1; reszta hipotez dotyczy pozostalych
											rozbieznosci.
										</span>
									)}
									{stalledApply.stillFlagged === null && (
										<span className="block text-muted-foreground">
											Zbior byl za duzy, zeby porownac konkretne wiersze — ponizej sama zmiana
											licznikow. Uwaga: licznik moze rosnac przez NOWE wiersze dociagniete w tle
											przez robota, wiec sam w sobie nie dowodzi, ze wysylka zawiodla.
										</span>
									)}
									{overview.snapshotMode === "aggregate" ? (
										<>
											{stalledApply.missing && (
												<span className="block">
													<span className="font-medium">
														Braki: {stalledApply.missing.before} → {stalledApply.missing.after} —
														klient NIE WIDZI naszych wierszy.
													</span>{" "}
													Agregat laczy sie u niego z TOWR i filtruje po t.BLOZ07, wiec wiersz bez
													odpowiednika w TOWR wypada na joinie. Doslij{" "}
													<span className="font-mono">towr</span> PRZED kzak.
												</span>
											)}
											{stalledApply.different && (
												<span className="block">
													<span className="font-medium">
														Roznice: {stalledApply.different.before} →{" "}
														{stalledApply.different.after} — klient ma prawdopodobnie NADMIAROWE
														wiersze.
													</span>{" "}
													Wysylka to wylacznie INSERT/UPDATE, zero DELETE. Kliknij wiersz w tabeli
													ponizej — szczegoly kubelka pokaza, ktory wiersz jest nadmiarowy.
												</span>
											)}
										</>
									) : (
										<span className="block">
											W trybie <span className="font-mono">row</span> klient jest odpytywany po
											kluczu, BEZ joinow — wiec brakujaca tabela zalezna niczego tu nie tlumaczy.
											Sprawdz raczej: czy rozniaca sie kolumna jest w mapowaniu (niezmapowana NIE
											jest wysylana, a mimo to bierze udzial w porownaniu) oraz czy klient nie
											nadpisuje jej po swojej stronie.
										</span>
									)}
								</span>
							}
							onDismiss={() => setStalledApply(null)}
						/>
					)}

					{summaryQuery.data?.lastError && (
						<NoticeBanner
							tone="warning"
							title="Ostatnia proba przeliczenia padla (dane z ostatniego sukcesu)"
							description={`Blad: ${summaryQuery.data.lastError}. Kliknij „Przelicz teraz", zeby sprobowac ponownie.`}
						/>
					)}

					<div className="flex flex-wrap items-center gap-2 max-md:[&>button]:h-9">
						<div className="inline-flex shrink-0 rounded-md border overflow-hidden max-md:max-w-full max-md:overflow-x-auto max-md:[&>button]:min-h-9 max-md:[&>button]:shrink-0 max-md:[&>button]:whitespace-nowrap">
							<button
								type="button"
								onClick={() => setKind("missing")}
								className={cn(
									"px-3 py-1 text-xs",
									kind === "missing" ? "bg-muted font-medium" : "text-muted-foreground",
								)}
							>
								Braki ({missing})
							</button>
							<button
								type="button"
								onClick={() => setKind("different")}
								className={cn(
									"px-3 py-1 text-xs border-l",
									kind === "different" ? "bg-muted font-medium" : "text-muted-foreground",
								)}
							>
								Roznice ({different})
							</button>
							{extra > 0 && (
								<button
									type="button"
									onClick={() => setKind("extra")}
									className={cn(
										"px-3 py-1 text-xs border-l",
										kind === "extra" ? "bg-muted font-medium" : "text-muted-foreground",
									)}
									title="Kubelki, ktore ma TYLKO klient. Diff ich nie widzi, bo iteruje po naszej stronie — ujawniaja je dopiero sumy. Nasza wysylka ich nie naprawi: robimy INSERT/UPDATE, zero DELETE."
								>
									Nadmiar u klienta ({extra})
								</button>
							)}
						</div>
						<Button
							size="sm"
							variant="outline"
							onClick={() => recompute.mutate()}
							disabled={recompute.isPending}
						>
							{recompute.isPending ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<RefreshCw className="mr-2 h-4 w-4" />
							)}
							<AutoStep
								step="A1"
								title="Krok 1 nocnego automatu (od 21:35 UTC): przeliczenie snapshotu dla kazdego aktywnego zapytania i kontraktu. Automat robi TYLKO to — wysylke ma w kroku A2. Ten przycisk dodatkowo od razu generuje wysylke, zebys widzial realne liczby zamiast samych kubelkow."
							/>
							Przelicz teraz
						</Button>
						<Button size="sm" variant="outline" onClick={() => void exportXlsx()}>
							<FileDown className="mr-2 h-4 w-4" /> Eksport do Excel
						</Button>
						<Button
							size="sm"
							variant="outline"
							onClick={() => resolvedSql.mutate()}
							disabled={resolvedSql.isPending}
							title="source_sql z podstawionymi blozami/aptekami/datami — do recznego sprawdzenia na bazie PG"
						>
							{resolvedSql.isPending ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<FileCode className="mr-2 h-4 w-4" />
							)}
							source_sql
						</Button>
						<Button
							size="sm"
							variant="outline"
							onClick={() => ilaktZero.mutate()}
							disabled={ilaktZero.isPending || different === 0}
							title="Ile roznic to wyzerowane partie: nasz ilakt = 0, a klient ma jeszcze stan. Pomaga ocenic, z czego sklada sie masa upsertow."
						>
							{ilaktZero.isPending ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<FileCode className="mr-2 h-4 w-4" />
							)}
							Ile to wyzerowane partie
						</Button>
						<Button
							size="sm"
							variant="default"
							onClick={() => applyScript.mutate()}
							disabled={applyScript.isPending}
							title="Podglad = apply: idempotentny upsert detalu (INSERT-if-not-exists + UPDATE, zero DELETE). Dokladnie to wykona job FM2."
						>
							{applyScript.isPending ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<FileCode className="mr-2 h-4 w-4" />
							)}
							Wygeneruj wysyłkę
						</Button>
						<Button
							size="sm"
							variant="outline"
							onClick={() => applySelect.mutate()}
							disabled={applySelect.isPending}
							title="SQL kontrolny: wypisuje klucze z wysylki, ktorych nasze zrodlo NIE zwraca. Uruchom na bazie PG — oczekiwany wynik to 0 wierszy."
						>
							{applySelect.isPending ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<FileCode className="mr-2 h-4 w-4" />
							)}
							SQL kontrolny
						</Button>
						<Button
							size="sm"
							variant="outline"
							onClick={() => applyRun.mutate(true)}
							disabled={applyRun.isPending}
							title="Dry-run: policz i zaloguj co poleci — NIE dotyka bazy klienta"
						>
							{applyRun.isPending ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<FileCode className="mr-2 h-4 w-4" />
							)}
							Dry-run wysyłki
						</Button>
						<Button
							size="sm"
							variant="outline"
							className="border-status-warning-border text-status-warning-text"
							onClick={() => setConfirmSend(true)}
							disabled={applyRun.isPending}
							title="REALNY apply: wykonuje upsert na bazie klienta. Wymaga Kontroluj + Wyślij do klienta i braku rozjazdu."
						>
							<FileCode className="mr-2 h-4 w-4" />
							<AutoStep
								step="A2"
								title="Krok 2 nocnego automatu, zaraz po A1: wysylka do klienta. Automat robi go TYLKO dla zapytan z flaga automat, tylko gdy sa ISTOTNE rozjazdy (sam dgakt nie wystarcza) i tylko w granicach limitu upsertow. Recznie ten przycisk nie sprawdza flagi automat ani limitu."
							/>
							{sendScope.sendLabel}
						</Button>
						<Button
							size="sm"
							variant="outline"
							onClick={() => prepareBatch.mutate()}
							disabled={sendScope.batchDisabled || prepareBatch.isPending || confirmBatch !== null}
							title={sendScope.batchTitle}
						>
							{prepareBatch.isPending || confirmBatch !== null ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<FileCode className="mr-2 h-4 w-4" />
							)}
							Wyslij partiami (apteka po aptece)
						</Button>
						{recompute.isPending && (
							<span className="text-xs text-muted-foreground">
								Przeliczenie moze potrwac chwile (per apteka).
							</span>
						)}
					</div>

					{/* Zawezenie w osobnej linii z opisem — jako waskie pole miedzy przyciskami
					    bylo niezauwazalne (placeholder sie urywal) i uzytkownik nie wiedzial,
					    ze podglad dla jednej apteki w ogole istnieje. */}
					<div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border px-2 py-1.5">
						<span className="text-xs text-muted-foreground">
							Zawez do jednej apteki — podglady <span className="font-medium">oraz</span> realna{" "}
							<span className="font-medium">wysylke do klienta</span>:
						</span>
						<input
							value={selectIdfirm}
							onChange={(e) => setSelectIdfirm(e.target.value.replace(/\D/g, ""))}
							placeholder="idfirm"
							inputMode="numeric"
							list="sfy-kontrola-idfirm"
							className="h-8 w-32 rounded-md border border-input bg-background px-2 text-xs max-md:h-9"
						/>
						<datalist id="sfy-kontrola-idfirm">
							{idfirmSuggestions.map((v) => (
								<option key={v} value={v} />
							))}
						</datalist>
						<span className="text-xs text-muted-foreground">
							{selectIdfirm
								? "· wszystko ponizej dotyczy TYLKO tej apteki — z dry-run i wysylka wlacznie"
								: "· puste = caly kontrakt"}
						</span>
					</div>

					{resolvedSql.data && (
						<div className="space-y-1">
							<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
								<span className="text-xs text-muted-foreground">
									source_sql (podstawione: {resolvedSql.data.blozCount} bloz ·{" "}
									{resolvedSql.data.unitcodeCount} aptek) — skopiuj i uruchom na PG
								</span>
								<div className="flex gap-2 max-md:[&>button]:h-9">
									<Button
										size="sm"
										variant="outline"
										onClick={() => void navigator.clipboard.writeText(resolvedSql.data?.sql ?? "")}
									>
										Kopiuj
									</Button>
									<Button size="sm" variant="outline" onClick={downloadResolvedSql}>
										<FileDown className="mr-2 h-4 w-4" /> Pobierz .sql
									</Button>
								</div>
							</div>
							<textarea
								className="w-full resize-y rounded-md border border-border bg-muted/30 px-2 py-1.5 font-mono text-[11px]"
								rows={12}
								readOnly
								value={resolvedSql.data.sql}
							/>
						</div>
					)}

					{applySelect.data && (
						<div className="space-y-1">
							<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
								<span className="text-xs text-muted-foreground">
									Kontrola {applySelect.data.rowCount} kluczy z wysyłki — uruchom na naszej bazie
									PG. <span className="font-medium">Oczekiwany wynik: 0 wierszy.</span> Każdy
									zwrócony klucz poleci do klienta, mimo że nie wychodzi z naszego źródła.
								</span>
								<Button
									size="sm"
									variant="outline"
									onClick={downloadApplySelectSql}
									disabled={applySelect.data.rowCount === 0}
									className="max-md:h-9"
								>
									<FileDown className="mr-2 h-4 w-4" /> Pobierz .sql
								</Button>
							</div>
							<textarea
								className="w-full resize-y rounded-md border border-border bg-muted/30 px-2 py-1.5 font-mono text-[11px]"
								rows={12}
								readOnly
								value={applySelect.data.sql}
							/>
						</div>
					)}

					{applyScript.data && (
						<div className="space-y-1">
							<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
								<span className="text-xs text-muted-foreground">
									{applyScript.data.statementCount}{" "}
									{applyScript.data.statementCount === 1 ? "upsert" : "upsertow"} detalu
									(idempotentne, zero DELETE; podglad = to, co wykona job FM2)
									{selectIdfirm && (
										<span className="font-medium text-status-warning-text">
											{" "}
											· tylko apteka {selectIdfirm}
										</span>
									)}
									{applyScript.data.unchangedSkipped > 0 && (
										<span title="Wiersze, ktore u klienta wygladaja identycznie — ich wysylka niczego by nie zmienila. Agregat stanu kubelkuje po saldzie, wiec jedna sztuka rozjazdu rozwija sie na wszystkie historyczne dostawy produktu.">
											{" "}
											· odsiano {applyScript.data.unchangedSkipped} niezmienionych
										</span>
									)}
									{applyScript.data.skippedNoBloz > 0 && (
										<span
											className="font-medium text-status-warning-text"
											title="Wiersze bez bloz07 sa wykluczone z wysylki — producent analizuje po blozach, wiec zakup bez identyfikacji produktu nic mu nie mowi."
										>
											{" "}
											· pominieto {applyScript.data.skippedNoBloz} bez bloz07
										</span>
									)}
								</span>
								<Button
									size="sm"
									variant="outline"
									onClick={downloadApplySql}
									disabled={applyScript.data.statementCount === 0}
									className="max-md:h-9"
								>
									<FileDown className="mr-2 h-4 w-4" /> Pobierz .sql
								</Button>
							</div>
							<ApplyScopeLine
								scope={applyScript.data.scope}
								statements={applyScript.data.statementCount}
							/>
							{applyScript.data.notes?.map((n) => (
								<div
									key={n}
									className="rounded-md border border-status-warning-border bg-status-warning-bg px-2 py-1.5 text-xs text-status-warning-text"
								>
									{n}
								</div>
							))}
							{!applyScript.data.aligned && (
								<div className="rounded-md border border-status-warning-border bg-status-warning-bg px-2 py-1.5 text-xs text-status-warning-text">
									Uwaga: liczba rozwinietych wierszy nie zgadza sie z agregatem — nie wysylac na
									slepo, zdiagnozowac rozjazd (bloz07/miesiac/anonimizacja).
								</div>
							)}
							{overview.snapshotMode === "row" &&
								applyScript.data.statementCount !== missing + different && (
									<div className="rounded-md border border-status-warning-border bg-status-warning-bg px-2 py-1.5 text-xs text-status-warning-text">
										Uwaga: tryb <span className="font-mono">row</span> wysyla 1 upsert na
										rozbieznosc, wiec powinno byc {missing + different} ({missing} braki +{" "}
										{different} roznice), a jest {applyScript.data.statementCount}. Nie wysylaj —
										kliknij „Przelicz teraz" i wygeneruj ponownie; jesli rozjazd zostaje, zglos to
										(liczby pochodza z tego samego snapshotu, wiec nie powinny sie roznic).
									</div>
								)}
							<textarea
								className="w-full resize-y rounded-md border border-border bg-muted/30 px-2 py-1.5 font-mono text-[11px]"
								rows={12}
								readOnly
								value={applyScript.data.sql || "-- brak wierszy do wyslania"}
							/>
						</div>
					)}

					{applyRun.data && (
						<div
							className={cn(
								"rounded-md border px-2 py-1.5 text-xs",
								applyRun.data.status === "ok"
									? "border-status-success-border bg-status-success-bg text-status-success-text"
									: applyRun.data.status === "dry_run"
										? "border-status-info-border bg-status-info-bg text-status-info-text"
										: "border-status-error-border bg-status-error-bg text-status-error-text",
							)}
						>
							<div className="font-medium">
								{applyRun.data.dryRun ? "Dry-run" : "Wysyłka"} — status: {applyRun.data.status} (run
								#{applyRun.data.runId})
							</div>
							<div>
								{applyRun.data.dryRun
									? `poleci ${applyRun.data.statementCount} upsertow (nic nie wykonano u klienta)`
									: `wykonano ${applyRun.data.appliedCount} / ${applyRun.data.statementCount} upsertow`}
								{applyRun.data.durationMs != null && ` · ${fmtDuration(applyRun.data.durationMs)}`}
								{applyRun.data.unchangedSkipped > 0 &&
									` · odsiano ${applyRun.data.unchangedSkipped} niezmienionych`}
								{applyRun.data.skippedNoBloz > 0 &&
									` · pominieto ${applyRun.data.skippedNoBloz} bez bloz07`}
							</div>
							<ApplyScopeLine
								scope={applyRun.data.scope}
								statements={applyRun.data.statementCount}
							/>
							{applyRun.data.error && (
								<div className="mt-1 max-md:wrap-anywhere">{applyRun.data.error}</div>
							)}
						</div>
					)}

					{applyRunsQuery.data && applyRunsQuery.data.length > 0 && (
						<div className="space-y-1">
							<button
								type="button"
								onClick={() => setHistoriaZwinieta((v) => !v)}
								className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground max-md:min-h-9"
							>
								{historiaZwinieta ? (
									<ChevronRight className="h-3 w-3" />
								) : (
									<ChevronDown className="h-3 w-3" />
								)}
								Historia wysyłek (audyt)
								<span className="font-normal">({applyRunsQuery.data.length})</span>
							</button>
							{/* Historia rosnie z kazda wysylka (przy wysylce partiami o kilkadziesiat
							    wpisow naraz) i spychala tabele rozbieznosci poza ekran. Okno na ~7
							    ostatnich, reszta pod scrollem; naglowek przyklejony. */}
							{!historiaZwinieta && (
								<div className="max-h-56 overflow-auto rounded-md border border-border">
									<table className="w-full text-xs">
										<thead className="sticky top-0 z-10 bg-muted text-muted-foreground">
											<tr className="text-left">
												<th className="px-2 py-1 font-medium">Kiedy</th>
												<th className="px-2 py-1 font-medium">Tryb</th>
												<th className="px-2 py-1 font-medium">Status</th>
												<th className="px-2 py-1 font-medium">Upserty</th>
												<th className="px-2 py-1 font-medium">Czas</th>
												<th className="px-2 py-1 font-medium">Użytkownik</th>
												<th className="px-2 py-1 font-medium">Błąd</th>
											</tr>
										</thead>
										<tbody>
											{applyRunsQuery.data.map((run) => (
												<tr key={run.id} className="border-t last:border-b-0">
													<td className="px-2 py-1 whitespace-nowrap tabular-nums">
														{new Date(run.startedAt).toLocaleString("pl-PL")}
													</td>
													<td className="px-2 py-1">
														{run.dryRun ? "dry-run" : run.runBy == null ? "automat" : "realny"}
														{run.scopeIdfirm != null && (
															<span
																className="ml-1 text-status-warning-text"
																title="Wysylka zawezona do jednej apteki — nie objela calego kontraktu"
															>
																· apteka {run.scopeIdfirm}
															</span>
														)}
													</td>
													<td className="px-2 py-1">
														<Badge
															className={cn(
																"text-[10px]",
																run.status === "ok"
																	? TONE_CLASS.success
																	: run.status === "dry_run" || run.status === "skipped"
																		? TONE_CLASS.neutral
																		: run.status === "running"
																			? TONE_CLASS.warning
																			: TONE_CLASS.error,
															)}
														>
															{run.status}
														</Badge>
													</td>
													<td className="px-2 py-1 tabular-nums">
														{run.status === "skipped"
															? "—"
															: run.dryRun
																? run.statementCount
																: `${run.appliedCount}/${run.statementCount}`}
														{run.skippedNoBloz > 0 && (
															<span
																className="ml-1 text-muted-foreground"
																title="Wiersze bez bloz07 wykluczone z tej wysylki (poza licznikiem upsertow)"
															>
																(-{run.skippedNoBloz})
															</span>
														)}
													</td>
													<td className="px-2 py-1 whitespace-nowrap tabular-nums text-muted-foreground">
														{run.finishedAt
															? fmtDuration(Date.parse(run.finishedAt) - Date.parse(run.startedAt))
															: "—"}
													</td>
													<td className="px-2 py-1 tabular-nums">{run.runBy ?? "automat"}</td>
													<td
														className={cn(
															"px-2 py-1",
															run.status === "skipped"
																? "text-muted-foreground"
																: "text-status-error-text",
														)}
													>
														{run.error ?? ""}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					)}

					{overview.snapshotMode === "aggregate" && !bucketDetail.data && (
						<p className="text-xs text-muted-foreground">
							Klik w wiersz ponizej pokaze KONKRETNE wiersze tego kubelka (wraz z nadmiarowymi u
							klienta).
						</p>
					)}

					{bucketDetail.isPending && (
						<p className="flex items-center gap-2 text-xs text-muted-foreground">
							<Loader2 className="h-3 w-3 animate-spin" /> Pobieram obie strony kubelka...
						</p>
					)}

					{bucketDetail.data && (
						<BucketDetailPanel detail={bucketDetail.data} onClose={() => bucketDetail.reset()} />
					)}

					<DiffTable
						query={diffsQuery}
						kind={kind}
						onRowClick={
							overview.snapshotMode === "aggregate"
								? (diffId) => bucketDetail.mutate(diffId)
								: undefined
						}
						// Pozycje zakupowe dokumentu maja sens tylko wtedy, gdy wiersz JEST
						// dokumentem. Tak jest w trybach `row` ORAZ `completeness` — ten
						// drugi tez klucza po (idfirm, id) dokf, wiec dziala bez zmian.
						// W agregacie wiersz to kubelek i `onRowClick` zajmuje klikniecie.
						renderExpansion={
							(overview.snapshotMode === "row" || overview.snapshotMode === "completeness") &&
							overview.ourTable === "dokf"
								? (d) => {
										const idfirm = numerZKlucza(d.matchKey, "idfirm");
										const iddokf = numerZKlucza(d.matchKey, "id");
										if (idfirm === null || iddokf === null) return null;
										return (
											<DokfLinesPanel
												connectionId={overview.connectionId}
												contractId={overview.contractId}
												idfirm={idfirm}
												iddokf={iddokf}
											/>
										);
									}
								: undefined
						}
					/>
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Zamknij
					</Button>
				</DialogFooter>
			</DialogContent>

			<ConfirmDialog
				open={confirmSend}
				onOpenChange={setConfirmSend}
				title={sendScope.confirmTitle}
				description={`Wykona idempotentny upsert (INSERT-if-not-exists + UPDATE, zero DELETE) na bazie klienta dla ${overview.producerName ?? "producenta"} · ${row.ourTable} · ${overview.contractName}.${sendScope.scopeNote} Upewnij się, że producent jest wyłączony w FM1. Backend zablokuje, jeśli kontrakt nie ma Kontroluj+Wyślij lub jest rozjazd.`}
				variant="warning"
				confirmLabel={sendScope.sendLabel}
				isLoading={applyRun.isPending}
				onConfirm={() => applyRun.mutate(false)}
			/>

			<SfyBatchSendDialog
				firms={confirmBatch}
				onClose={() => setConfirmBatch(null)}
				sendOne={(idfirm) =>
					outboundApi.applySfy(row.connectionId, row.queryId, overview.contractId, false, idfirm)
				}
				onFinished={() => applyRunsQuery.refetch()}
			/>
		</Dialog>
	);
}

/** Szczegoly jednego kubelka agregatu: nasze wiersze vs klient, z zaznaczeniem
 *  ktora kolumna sie rozni i wierszami NADMIAROWYMI po stronie klienta. */
function BucketDetailPanel({ detail, onClose }: { detail: SfyBucketDetail; onClose: () => void }) {
	const keyCols = Object.keys(detail.rows[0]?.matchKey ?? detail.bucket);
	// Kolumne pokazujemy tylko, gdy JAKIKOLWIEK wiersz rozni sie blozem — inaczej
	// jest bez zwiazku i tylko zabiera miejsce w i tak szerokiej tabeli.
	const [pokazNaprawe, setPokazNaprawe] = useState(false);
	const pokazZmiane = detail.rows.some((r) =>
		r.differing.some((c) => c.toLowerCase() === "bloz07"),
	);
	// Kolumny wartosci: te, ktore gdziekolwiek sie roznia — plus reszta na koncu.
	const valueCols = useMemo(() => {
		const differing = new Set<string>();
		const rest = new Set<string>();
		for (const r of detail.rows) {
			for (const c of r.differing) differing.add(c);
			for (const c of Object.keys(r.ours ?? r.client ?? {})) rest.add(c);
		}
		for (const c of differing) rest.delete(c);
		return [...differing, ...rest];
	}, [detail.rows]);

	return (
		<div className="space-y-1 rounded-md border border-primary/40 bg-muted/20 p-2">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<span className="text-xs max-md:wrap-anywhere">
					<span className="font-medium">Kubelek:</span>{" "}
					{Object.entries(detail.bucket).map(([k, v]) => (
						<span key={k} className="mr-2 font-mono">
							{k}={v}
						</span>
					))}
					<span className="ml-2 text-status-error-text">{detail.differentCount} roznic</span>
					<span className="ml-2 text-status-warning-text">
						{detail.missingCount} brak u klienta
					</span>
					<span className="ml-2 text-status-warning-text">
						{detail.extraCount} nadmiarowych u klienta
					</span>
					{detail.truncated && <span className="ml-2 text-muted-foreground">· lista obcieta</span>}
				</span>
				<div className="flex items-center gap-2 max-md:flex-wrap max-md:[&>button]:h-9">
					{(detail.repair.statementCount > 0 || detail.repair.skipped.length > 0) && (
						<Button size="sm" variant="outline" onClick={() => setPokazNaprawe((v) => !v)}>
							{pokazNaprawe ? "Ukryj" : "Wygeneruj"} update naprawczy
							{detail.repair.statementCount > 0 && ` (${detail.repair.statementCount})`}
						</Button>
					)}
					<Button size="sm" variant="outline" onClick={onClose}>
						Zamknij szczegoly
					</Button>
				</div>
			</div>
			{detail.clientSql && (
				<details className="rounded-md border border-border">
					<summary className="cursor-pointer px-2 py-1 text-xs text-muted-foreground">
						Zapytanie puszczone u klienta
					</summary>
					<pre className="overflow-x-auto whitespace-pre-wrap border-t border-border px-2 py-1 font-mono text-[11px]">
						{detail.clientSql}
					</pre>
				</details>
			)}
			{pokazNaprawe && (
				<div className="space-y-1 rounded-md border border-status-warning-border bg-status-warning-bg p-2">
					<div className="text-xs text-status-warning-text">
						Waski UPDATE — tylko kolumny, ktore sie roznia I ktore wolno nadpisac. Do recznego
						uruchomienia u klienta; SFY tego nie wykona.
					</div>
					<div className="text-[11px] text-muted-foreground">
						md5 dostaje znacznik <span className="font-mono">FM2_&lt;klucz&gt;</span>, zeby recznie
						poprawiony wiersz dalo sie odroznic od tego, co wstawil stary system. Jesli FM1
						porownuje md5 klienta ze swoim, ta linia sprowokuje nadpisanie — wtedy ja usun.
					</div>
					{detail.repair.statementCount > 0 ? (
						<textarea
							className="w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px]"
							rows={10}
							readOnly
							value={detail.repair.sql}
						/>
					) : (
						<div className="text-xs font-medium text-status-warning-text">
							Nie ma czego naprawiac u klienta — zaden wiersz nie przeszedl kontroli kierunku.
						</div>
					)}
					{detail.repair.skipped.length > 0 && (
						<details className="text-[11px]">
							<summary className="cursor-pointer text-muted-foreground">
								Pominieto {detail.repair.skipped.length} — pokaz powody
							</summary>
							<ul className="mt-1 space-y-0.5 pl-4 text-muted-foreground">
								{detail.repair.skipped.map((p) => (
									<li key={p} className="list-disc">
										{p}
									</li>
								))}
							</ul>
						</details>
					)}
				</div>
			)}
			<div className="max-h-72 overflow-auto rounded-md border border-border bg-background">
				<table className="w-full text-xs">
					<thead className="sticky top-0 bg-muted">
						<tr className="text-left">
							{keyCols.map((k) => (
								<th key={k} className="p-2 font-medium whitespace-nowrap">
									{k}
								</th>
							))}
							<th className="p-2 font-medium whitespace-nowrap">status</th>
							{pokazZmiane && (
								<th
									className="p-2 font-medium whitespace-nowrap"
									title="Ostatnia zmiana blozu na KARCIE TOWARU w tej aptece. To ona rozstrzyga, czy rozjazd propagowac do klienta, czy naprawic u nas."
								>
									zmiana blozu w aptece
								</th>
							)}
							{valueCols.map((c) => (
								<th key={c} className="p-2 font-medium whitespace-nowrap">
									{c} (u nas → klient)
								</th>
							))}
						</tr>
					</thead>
					<tbody className="divide-y divide-border/40">
						{detail.rows.map((r) => (
							<tr key={keyCols.map((k) => r.matchKey[k]).join(":")}>
								{keyCols.map((k) => (
									<td key={k} className="p-2 font-mono whitespace-nowrap">
										{r.matchKey[k]}
									</td>
								))}
								{pokazZmiane && (
									<td className="p-2 whitespace-nowrap">
										<BlozChangeCell change={r.blozChange} />
									</td>
								)}
								<td className="p-2 whitespace-nowrap">
									{r.status === "roznica" ? (
										<span className="text-status-error-text">⚠ roznica</span>
									) : r.status === "brak_u_klienta" ? (
										<span className="text-status-warning-text">✗ brak u klienta</span>
									) : r.status === "nadmiar_u_klienta" ? (
										<span
											className="text-status-warning-text"
											title="Klient ma ten wiersz, my nie. Upsert go NIE usunie — trzeba go skasowac po jego stronie albo zaakceptowac."
										>
											○ nadmiar u klienta
										</span>
									) : (
										<span className="text-status-success-text">✓ zgodne</span>
									)}
								</td>
								{valueCols.map((c) => (
									<DiffValueCell
										key={c}
										ours={r.ours?.[c] ?? null}
										client={r.client === null ? undefined : (r.client?.[c] ?? null)}
										differs={r.differing.includes(c)}
									/>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

/**
 * Komorka „u nas → u klienta". Gdy roznica jest NIEWIDOCZNA (twarda spacja,
 * podwojna spacja, znak zerowej szerokosci), pokazuje ja jawnie — bez tego
 * komorka swieci sie na czerwono, a obie strony wygladaja identycznie.
 * Zgloszenie 2026-09-08: `$$$Cirrus Duo 6 tabl. VEDIM` po obu stronach.
 */
function DiffValueCell({
	ours,
	client,
	differs,
}: {
	ours: string | null;
	/** `undefined` = klient nie ma tego wiersza wcale (nie renderujemy strzalki). */
	client?: string | null;
	differs: boolean;
}) {
	// Opis jest BEZWARUNKOWY, gdy obie strony maja wartosc i sie roznia.
	// Poprzednia wersja pokazywala go tylko dla roznic uznanych za „niewidoczne"
	// i przez to milczala przy homoglifach (cyrylickie „С" w „Cirrus") oraz
	// polowie znakow formatujacych — czyli dokladnie tam, gdzie byla potrzebna.
	const { opis, show } = invisibleHint(ours, client ?? null, differs && client !== undefined);
	const pokaz = (v: string | null) => show(v) ?? "—";

	return (
		<td
			className={cn(
				"p-2 font-mono whitespace-nowrap",
				differs && "font-semibold text-status-error-text",
			)}
			title={opis ?? undefined}
		>
			{pokaz(ours)}
			{client !== undefined && <> → {pokaz(client)}</>}
			<InvisibleFlag opis={opis} />
		</td>
	);
}

function DiffTable({
	query,
	kind,
	onRowClick,
	renderExpansion,
}: {
	query: ReturnType<typeof useQuery<AggregateDiffRow[]>>;
	kind: DiffKind;
	/** Wejscie w kubelek (tylko agregat — w trybie row wiersz JEST juz detalem). */
	onRowClick?: (diffId: number) => void;
	/**
	 * Rozwijany podglad POD wierszem (tryb row). `null` = dla tego wiersza nie ma
	 * czego pokazac, wiec wiersz nie staje sie klikalny.
	 */
	renderExpansion?: (d: AggregateDiffRow) => ReactNode;
}) {
	const [expanded, setExpanded] = useState<number | null>(null);
	// Sortowanie po kolumnie klucza (klik w naglowek) — przy diagnozie jednej apteki
	// trzeba miec jej wiersze OBOK SIEBIE, a domyslny porzadek (dgakt) je rozrzuca.
	const [sortKey, setSortKey] = useState<string | null>(null);
	const [sortAsc, setSortAsc] = useState(true);

	// Domyslnie: roznice po NASZYM dgakt rosnaco (najstarszy u gory). Klucz dgakt
	// szukany case-insensitive (nazwa kolumny klienta bywa DGAKT/dgakt); wiersze bez
	// dgakt na dole. dgakt to ISO timestamp -> porownanie leksykalne = chronologiczne.
	const rows = useMemo(() => {
		const data = query.data ?? [];
		if (data.length === 0) return data;

		if (sortKey) {
			// Wartosci kluczy to teksty, ale idfirm/bloz07 sa liczbami — porownanie
			// leksykalne dawaloby 1000 < 999. Liczbowo, gdy obie strony sie parsuja.
			const dir = sortAsc ? 1 : -1;
			return [...data].sort((a, b) => {
				const av = a.matchKey[sortKey] ?? "";
				const bv = b.matchKey[sortKey] ?? "";
				const an = Number(av);
				const bn = Number(bv);
				if (av !== "" && bv !== "" && !Number.isNaN(an) && !Number.isNaN(bn)) {
					return (an - bn) * dir;
				}
				return av.localeCompare(bv, "pl") * dir;
			});
		}

		if (kind !== "different") return data;
		const dgaktKey = Object.keys(data[0].sourceValues ?? {}).find(
			(k) => k.toLowerCase() === "dgakt",
		);
		if (!dgaktKey) return data;
		return [...data].sort((a, b) => {
			const av = a.sourceValues?.[dgaktKey] ?? "";
			const bv = b.sourceValues?.[dgaktKey] ?? "";
			if (!av && !bv) return 0;
			if (!av) return 1;
			if (!bv) return -1;
			return av < bv ? -1 : av > bv ? 1 : 0;
		});
	}, [query.data, kind, sortKey, sortAsc]);

	const [materialOnly, setMaterialOnly] = useState(false);

	if (query.isLoading) {
		return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
	}
	if (rows.length === 0) {
		return <p className="text-sm text-muted-foreground">Brak wierszy dla „{kind}".</p>;
	}
	const shown = kind === "different" && materialOnly ? rows.filter(isMaterialDiff) : rows;
	const keyCols = Object.keys(rows[0].matchKey);
	const valueKeys = collectValueKeys(rows, kind);
	// Kolumna kontrolna z kartoteki apteki. Backend wypelnia ja tylko dla zapytan
	// projektujacych `idtowr` (tryb 'row'), wiec brak wartosci = nie ma czego pokazac.
	const showTowr = rows.some((r) => r.towrBloz07 != null);
	// Rozwiniecie ma sie ciagnac przez cala szerokosc tabeli — liczba kolumn
	// musi isc z tych samych zrodel co naglowek, inaczej rozjedzie sie po dodaniu
	// kolejnej kolumny.
	const liczbaKolumn = keyCols.length + (showTowr ? 1 : 0) + valueKeys.length;
	// NASZ bloz to `k.bloz07` — to, co fizycznie lezy u klienta. Rozjazd z kartoteka
	// jest normalny (dwie role bloz07), ale przy pustym k.bloz07 to jedyny trop.
	const ourBlozKey = valueKeys.find((k) => k.toLowerCase() === "bloz07");
	return (
		<div className="space-y-2">
			{kind === "different" && (
				<label className="flex items-center gap-2 text-xs text-muted-foreground max-md:min-h-9">
					<input
						type="checkbox"
						checked={materialOnly}
						onChange={(e) => setMaterialOnly(e.target.checked)}
						className="rounded border-input"
					/>
					Tylko istotne różnice (pomiń wiersze z samym <code className="font-mono">dgakt</code>)
				</label>
			)}
			<div className="max-h-[50vh] overflow-auto rounded-md border">
				<table className="w-full text-xs">
					<thead className="sticky top-0 bg-muted/70">
						<tr className="text-left">
							{keyCols.map((k) => (
								<th key={k} className="p-2 font-medium whitespace-nowrap">
									<button
										type="button"
										onClick={() => {
											if (sortKey === k) setSortAsc((v) => !v);
											else {
												setSortKey(k);
												setSortAsc(true);
											}
										}}
										className="inline-flex items-center gap-1 hover:underline"
										title={`Sortuj po ${k}`}
									>
										{k}
										{sortKey === k && <span>{sortAsc ? "▲" : "▼"}</span>}
									</button>
								</th>
							))}
							{showTowr && (
								<th
									className="p-2 font-medium whitespace-nowrap"
									title="bloz07 z kartoteki apteki (towr), po k.idtowr — prawda o produkcie. Po tej kolumnie liczy sie zakres kontraktu; do klienta wysylamy k.bloz07 obok."
								>
									bloz07 z towr
								</th>
							)}
							{valueKeys.map((vk) => (
								<th key={vk} className="p-2 font-medium whitespace-nowrap">
									{/* braki: klient NIE MA tego kubelka (dest_values=NULL), wiec pokazujemy
									    wylacznie nasze wartosci — naglowek nazywa to wprost. */}
									{kind === "different"
										? `${vk} (u nas → klient)`
										: kind === "extra"
											? `${vk} (u klienta)`
											: `${vk} (u nas)`}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{shown.map((d) => {
							const immaterial = kind === "different" && !isMaterialDiff(d);
							// Rozwiniecie liczymy raz: jego brak decyduje, czy wiersz jest klikalny.
							const rozwiniecie = renderExpansion?.(d) ?? null;
							const klikalny = Boolean(onRowClick) || rozwiniecie !== null;
							return (
								<Fragment key={d.id}>
									<tr
										className={cn(
											"border-t",
											immaterial && "opacity-45",
											klikalny && "cursor-pointer hover:bg-muted/40",
										)}
										onClick={
											onRowClick
												? () => onRowClick(d.id)
												: rozwiniecie !== null
													? () => setExpanded((v) => (v === d.id ? null : d.id))
													: undefined
										}
										title={
											onRowClick
												? "Pokaz konkretne wiersze tego kubelka"
												: rozwiniecie !== null
													? "Pokaz pozycje zakupowe tego dokumentu"
													: undefined
										}
									>
										{keyCols.map((k) => (
											<td key={k} className="p-2 font-mono whitespace-nowrap">
												{d.matchKey[k]}
											</td>
										))}
										{showTowr && (
											<td className="p-2 font-mono whitespace-nowrap">
												<TowrBlozCell
													towr={d.towrBloz07}
													ours={ourBlozKey ? (d.sourceValues?.[ourBlozKey] ?? null) : null}
												/>
											</td>
										)}
										{valueKeys.map((vk) => {
											const src = d.sourceValues?.[vk] ?? "";
											const dst = d.destValues?.[vk] ?? "";
											const differs = kind === "different" && (d.diffColumns ?? []).includes(vk);
											// Ta tabela ma wlasny uklad kolorow, ale diagnoza roznicy
											// jest ta sama co w szczegolach kubelka — wspolny helper.
											const { opis, show } = invisibleHint(src, dst, differs);
											return (
												<td key={vk} className="p-2 whitespace-nowrap">
													{kind === "extra" ? (
														dst
													) : kind !== "different" ? (
														src
													) : differs ? (
														<span title={opis ?? undefined}>
															<span className="text-status-error-text">{show(src)}</span>
															<span className="text-muted-foreground"> → </span>
															<span>{show(dst)}</span>
															<InvisibleFlag opis={opis} />
														</span>
													) : (
														<span className="text-muted-foreground">{src}</span>
													)}
												</td>
											);
										})}
									</tr>
									{rozwiniecie !== null && expanded === d.id && (
										<tr>
											<td colSpan={liczbaKolumn} className="p-0">
												{rozwiniecie}
											</td>
										</tr>
									)}
								</Fragment>
							);
						})}
					</tbody>
				</table>
				{rows.length >= 200 && (
					<p className="p-2 text-xs text-muted-foreground">
						Pokazano pierwsze 200. Pelna lista w eksporcie do Excela.
					</p>
				)}
				{shown.length === 0 && (
					<p className="p-2 text-xs text-muted-foreground">
						Brak istotnych różnic — wszystkie {rows.length} to tylko dgakt.
					</p>
				)}
			</div>
		</div>
	);
}

/**
 * Sumy trzech miar kontraktu: stan / zakup / sprzedaz, nasza strona vs klient.
 *
 * Dlaczego tu, a nie w oknie pojedynczej tabelki: to sa pytania o KONTRAKT („ile
 * jest na stanie u klienta, a ile u nas"), nie o zapytanie. Kazda miara to jeden
 * istniejacy agregat z IDENTYCZNYM filtrem po obu stronach — porownanie jest jablko
 * do jablka i nie kosztuje ani jednego zapytania, bo sumy zapisuje snapshot.
 *
 * `fedBy` jest tu najwazniejsze: miare MIERZY jedno zapytanie, ale ZMIENIC ja moze
 * kazde piszace do tej samej tabeli klienta. Do kzak pisza kzak_stan, kzak_sprz
 * i kzak_korz naraz — stad „wyslalem kzak_stan, a stan sie nie zgadza".
 *
 * Jeden panel na pare (polaczenie, kontrakt). Dwa kontrakty producenta to dwa panele
 * z nazwa — nie suma (rozne okresy, a stan liczylby ten sam magazyn dwa razy).
 */
function MeasureTotalsPanel({ target }: { target: SfyMeasureTarget }) {
	const { connectionId, contractId } = target;
	const totals = useQuery({
		queryKey: ["sfy", "measure-totals", connectionId, contractId],
		queryFn: () => outboundApi.getSfyMeasureTotals(connectionId, contractId),
		staleTime: 5 * 60 * 1000,
	});
	if (!totals.data || totals.data.length === 0) return null;
	const fmt = (v: number) =>
		v.toLocaleString("pl-PL", { maximumFractionDigits: Number.isInteger(v) ? 0 : 2 });
	// Wiek najstarszej z miar. Bez tego liczba sprzed tygodnia wyglada jak dzisiejsza —
	// a najbardziej trzeba jej ufac wtedy, gdy cos poszlo nie tak i wysylka stoi.
	const oldest = totals.data.reduce<string | null>(
		(acc, m) => (m.computedAt && (!acc || m.computedAt < acc) ? m.computedAt : acc),
		null,
	);
	const ageH = snapshotAgeHours(oldest, Date.now());
	const stale = ageH != null && ageH > SNAPSHOT_STALE_HOURS;
	return (
		<div className="mb-3 rounded-md border border-border max-md:overflow-x-auto">
			<div className="border-b border-border bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
				Sumy kontraktu{" "}
				<span className="font-medium text-foreground">
					{target.label} · {target.contractName}
				</span>{" "}
				— nasza strona vs SFY.{" "}
				{oldest ? (
					<span
						className={cn(stale && "font-medium text-status-warning-text")}
						title="Sumy pochodza z ostatniego UDANEGO snapshotu. Snapshot chodzi niezaleznie od wysylki, wiec te liczby sa aktualne takze wtedy, gdy wysylka jest zablokowana — chyba ze to sam snapshot przestal sie udawac."
					>
						Pomiar: {new Date(oldest).toLocaleString("pl-PL")}
						{stale && " · ⚠ nieswiezy"}
					</span>
				) : (
					<span className="text-status-warning-text">Brak pomiaru — snapshot nie przeliczony</span>
				)}
			</div>
			<table className="w-full text-xs max-md:min-w-[54rem]">
				<thead>
					<tr className="border-b text-left text-muted-foreground">
						<th className="p-2 font-medium">Miara</th>
						<th className="p-2 font-medium text-right">u nas</th>
						<th className="p-2 font-medium text-right">w SFY</th>
						<th className="p-2 font-medium text-right">roznica</th>
						<th
							className="p-2 font-medium text-right"
							title="Wartosc po NASZYCH cenach ex-factory z cennikow podpietych do kontraktu — te same ceny co w rozliczeniu. Obie strony wyceniane z tych samych kubelkow (apteka, bloz, miesiac), cena z 1. dnia miesiaca."
						>
							wartosc ex-f. u nas
						</th>
						<th className="p-2 font-medium text-right">wartosc ex-f. w SFY</th>
						<th className="p-2 font-medium text-right">roznica zl</th>
						<th className="p-2 font-medium">zakres</th>
						<th className="p-2 font-medium">mierzy</th>
						<th className="p-2 font-medium">zasilaja</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-border/40">
					{totals.data.map((m) => (
						<tr key={m.measure}>
							<td className="p-2">
								<span className="font-medium">{m.measure}</span>{" "}
								<span className="text-muted-foreground">{m.label}</span>
							</td>
							<td className="p-2 text-right font-mono">{m.ours != null ? fmt(m.ours) : "—"}</td>
							<td className="p-2 text-right font-mono">{m.client != null ? fmt(m.client) : "—"}</td>
							<td className="p-2 text-right font-mono">
								<MeasureDiff ours={m.ours} client={m.client} format={fmt} />
							</td>
							<td className="p-2 text-right font-mono">
								<ExfactoryValue value={m.oursExfactoryValue} missing={m.oursMissingPriceCount} />
							</td>
							<td className="p-2 text-right font-mono">
								<ExfactoryValue
									value={m.clientExfactoryValue}
									missing={m.clientMissingPriceCount}
								/>
							</td>
							<td className="p-2 text-right font-mono">
								<MeasureDiff
									ours={m.oursExfactoryValue}
									client={m.clientExfactoryValue}
									format={formatPln}
								/>
							</td>
							<td
								className={cn(
									"p-2",
									m.range.startsWith("UWAGA")
										? "font-medium text-status-error-text"
										: "text-muted-foreground",
								)}
							>
								{m.range}
							</td>
							<td className="p-2 font-mono text-muted-foreground" title={m.column}>
								{m.measuredBy}
							</td>
							<td
								className="p-2 text-muted-foreground"
								title="Zapytania piszace do TEJ SAMEJ tabeli klienta. Kazde z nich moze zmienic te miare, nie tylko to, ktore ja mierzy."
							>
								{m.fedBy.join(", ")}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

const formatPln = (v: number) =>
	v.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Roznica nasza minus SFY, w nawiasie udzial w naszej sumie. Powyzej 1 % to juz nie szum. */
function MeasureDiff({
	ours,
	client,
	format,
}: {
	ours: number | null;
	client: number | null;
	format: (v: number) => string;
}) {
	if (ours == null || client == null) return <>—</>;
	const diff = ours - client;
	// Wartosc w zlotowkach to suma iloczynow float — grosz roznicy to zaokraglenie, nie rozjazd.
	if (Math.abs(diff) < 0.005) return <span className="text-status-success-text">✓ zgodne</span>;
	const pct = ours ? (diff / ours) * 100 : null;
	return (
		<span
			className={cn(
				pct != null && Math.abs(pct) >= 1 ? "text-status-error-text" : "text-status-warning-text",
			)}
		>
			{diff > 0 ? "+" : ""}
			{format(diff)}
			{pct != null && ` (${pct > 0 ? "+" : ""}${pct.toFixed(2)}%)`}
		</span>
	);
}

/** Wartosc ex-factory jednej strony; brak ceny sygnalizujemy, bo zaniza wartosc. */
function ExfactoryValue({ value, missing }: { value: number | null; missing: number | null }) {
	if (value == null) {
		return (
			<span
				className="text-muted-foreground"
				title="Brak wyceny: stan nie jest obrotem, albo snapshot policzono przed wprowadzeniem wyceny — przelicz snapshot."
			>
				—
			</span>
		);
	}
	return (
		<>
			{formatPln(value)}
			{missing != null && missing > 0 && (
				<div
					className="text-[11px] text-status-warning-text"
					title="Produkty z ruchem, dla ktorych cenniki podpiete do kontraktu nie maja ceny (takze bloz 0000000 z pozycji zakupu). Ich ilosc nie wchodzi do wartosci."
				>
					bez ceny: {missing.toLocaleString("pl-PL")} prod.
				</div>
			)}
		</>
	);
}

/**
 * Zmiana blozu na karcie towaru — PODSTAWA decyzji, czy rozjazd propagowac.
 *
 * Brak zdarzenia to nie brak danych, tylko mocna informacja: skoro apteka niczego nie
 * zmieniala, rozjazd wzial sie u NAS (defekt zrodla albo zmiana projekcji k.bloz07 vs
 * t.bloz07) — i wtedy propagowanie go do klienta tworzyloby szkode, ktorej w aptece nie ma.
 */
function BlozChangeCell({ change }: { change: SfyBlozChange | null }) {
	if (!change) {
		return (
			<span
				className="text-status-warning-text"
				title="Karta towaru w tej aptece NIE byla zmieniana. Rozjazd blozu pochodzi wiec z naszej strony — sprawdz zrodlo, zanim cokolwiek wyslesz."
			>
				brak zmiany w aptece
			</span>
		);
	}
	// „utrata" i „ponowne_uzycie" to przypadki, przy ktorych czlowiek ma sie zatrzymac:
	// pierwszy niszczy identyfikacje, drugi podmienia produkt pod ta sama karta.
	const tone =
		change.typ === "utrata" || change.typ === "ponowne_uzycie"
			? "text-status-error-text font-medium"
			: "text-muted-foreground";
	const opis =
		change.typ === "utrata"
			? "Apteka WYCZYSCILA bloz na karcie. Propagacja przeniesie te strate do klienta — swiadomie, bo taka jest teraz prawda w aptece."
			: change.typ === "ponowne_uzycie"
				? "Karta dostala bloz, ale zmienila sie NAZWA produktu — historia u klienta odnosi sie do czegos innego. Nie wysylac bez sprawdzenia."
				: change.typ === "nadanie"
					? "Karta dostala bloz (wczesniej pusty). Klient ma nieaktualna wartosc — update jest zasadny."
					: "Bloz zmieniony z jednego realnego na inny. Klient ma nieaktualna wartosc — update jest zasadny.";
	return (
		<span className={tone} title={opis}>
			{change.typ}
			<span className="ml-1 font-mono text-[11px] text-muted-foreground">
				{change.fromBloz07 || "—"} → {change.toBloz07 || "—"}
			</span>
			{change.changeDate && (
				<span className="ml-1 text-[11px] text-muted-foreground">({change.changeDate})</span>
			)}
		</span>
	);
}

/** Rodzaj rozbieznosci. `extra` to kubelki, ktore ma TYLKO klient — diff iteruje
 *  po naszej stronie, wiec ich nie widzi, a wysylka ich nie naprawi (zero DELETE). */
type DiffKind = "missing" | "different" | "extra";

/** Etykiety miar agregatu — same nazwy kolumn nic operatorowi nie mowia. */
const MEASURE_LABELS: Record<string, string> = {
	suma_ilosp: "sprzedaz",
	suma_ilzkl: "zakupy",
	suma_ilakt: "stan",
	liczba_wierszy: "wierszy",
	wartosc_exfactory: "wartosc ex-factory zl",
	bez_ceny_exfactory: "produktow bez ceny",
};

/**
 * Sumy obu stron z ostatniego snapshotu. Odpowiadaja na pytanie, ktorego licznik
 * rozbieznosci nie tyka: CZY te rozbieznosci znacza. 8990 roznic przy sumach zgodnych
 * co do 0,03 % to szum; 100 roznic przy 30 % luki to pozar.
 *
 * Liczone przy snapshocie, bo obie strony sa wtedy juz w pamieci — na zadanie
 * kosztowalyby tyle co pelne przeliczenie (petla per apteka po hipertabeli).
 */
function TotalsLine({
	ours,
	client,
}: {
	ours: Record<string, number> | null;
	client: Record<string, number> | null;
}) {
	if (!ours || Object.keys(ours).length === 0) return null;
	const fmt = (v: number) =>
		Number.isInteger(v)
			? v.toLocaleString("pl-PL")
			: v.toLocaleString("pl-PL", {
					maximumFractionDigits: 2,
				});
	return (
		<div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
			{Object.entries(ours).map(([key, mine]) => {
				const theirs = client?.[key];
				const diff = theirs != null ? mine - theirs : null;
				// Procent liczymy od NASZEJ strony — to ona jest odniesieniem.
				const pct = diff != null && mine !== 0 ? (diff / mine) * 100 : null;
				return (
					<span key={key}>
						<span className="font-medium">{MEASURE_LABELS[key] ?? key}</span>{" "}
						<span className="font-mono">{fmt(mine)}</span>
						<span> / </span>
						<span className="font-mono">{theirs != null ? fmt(theirs) : "—"}</span>
						{diff != null && diff !== 0 && (
							<span
								className={cn(
									"ml-1 font-mono",
									pct != null && Math.abs(pct) >= 1
										? "text-status-error-text"
										: "text-status-warning-text",
								)}
								title="Roznica nasza minus klienta, w nawiasie udzial w naszej sumie. Powyzej 1 % to juz nie szum."
							>
								({diff > 0 ? "+" : ""}
								{fmt(diff)}
								{pct != null && `, ${pct > 0 ? "+" : ""}${pct.toFixed(2)}%`})
							</span>
						)}
						{diff === 0 && <span className="ml-1 text-status-success-text">✓</span>}
					</span>
				);
			})}
		</div>
	);
}

/**
 * Znacznik kroku nocnego automatu. Nocny przebieg to DOKLADNIE dwa kroki, na tych
 * samych endpointach co te przyciski: A1 = przeliczenie snapshotu
 * (`run_all_scheduled_aggregates`), A2 = wysylka (`run_all_auto_applies`). Dzieki
 * temu widac, ktore rzeczy z tego okna automat robi sam — i co sie zmieni, jesli
 * ktoras z nich ruszymy.
 */
function AutoStep({ step, title }: { step: "A1" | "A2"; title: string }) {
	return (
		<span
			className="mr-1.5 rounded bg-muted px-1 py-0.5 font-mono text-[10px] text-muted-foreground"
			title={title}
		>
			{step}
		</span>
	);
}

/**
 * Ksztalt zbioru do wyslania. Odpowiada na dwa pytania, ktorych sama liczba upsertow
 * nie tyka: czy rozjechala sie JEDNA apteka, czy wszystkie po trochu (srednia vs
 * najgorsza), i czy dosylamy dzisiejsza delte, czy naprawiamy zaleglosc (min dgakt).
 * Swiadomie po `dgakt`, nie po `datzk`/`datsp` — towar potrafi lezec w aptece dwa
 * lata, wiec data zakupu nic o wieku ROZJAZDU nie mowi.
 */
function ApplyScopeLine({ scope, statements }: { scope?: SfyApplyScope; statements: number }) {
	if (!scope) return null;
	const parts: string[] = [];
	if (scope.firms > 0) {
		parts.push(`${scope.firms} ${scope.firms === 1 ? "apteka" : "aptek"}`);
		parts.push(`średnio ${Math.round(statements / scope.firms)}/aptekę`);
		if (scope.topFirm) {
			parts.push(`najwięcej ${scope.maxPerFirm} (apteka ${scope.topFirm})`);
		}
	}
	const stamp = (v: string) => v.slice(0, 16).replace("T", " ");
	if (scope.dgaktMin) {
		parts.push(
			scope.dgaktMax && scope.dgaktMax !== scope.dgaktMin
				? `dgakt ${stamp(scope.dgaktMin)} → ${stamp(scope.dgaktMax)}`
				: `dgakt ${stamp(scope.dgaktMin)}`,
		);
	}
	if (parts.length === 0) return null;
	return (
		<div
			className="text-xs text-muted-foreground"
			title="Najstarszy dgakt mowi, jak stare rozjazdy naprawia ta wysylka. Duza roznica miedzy srednia a najwieksza apteka = problem siedzi w jednej aptece, nie jest systemowy."
		>
			{parts.join(" · ")}
		</div>
	);
}

/**
 * `t.bloz07` obok naszego `k.bloz07`. Podswietlamy TYLKO rozjazd — zgodnosc jest
 * norma i nie ma po co krzyczec. Dwa przypadki warte uwagi:
 *  - nasz bloz pusty, kartoteka ma wartosc → wiersz jest wykluczony z wysylki, a to
 *    mowi, o ktory produkt chodzi (zwykle blad w FM1 albo wiersz skasowany w aptece),
 *  - obie wartosci sa, ale rozne → dwie role bloz07, opisane w doc/outbound/.
 */
function TowrBlozCell({ towr, ours }: { towr: string | null; ours: string | null }) {
	if (towr == null) {
		return <span className="text-muted-foreground">—</span>;
	}
	const norm = (v: string | null) => (v ?? "").trim();
	if (norm(towr) === norm(ours)) {
		return <span className="text-muted-foreground">{towr}</span>;
	}
	return (
		<span
			className="font-medium text-status-warning-text"
			title={
				norm(ours) === ""
					? "Nasz k.bloz07 jest pusty, a kartoteka apteki zna produkt — ten wiersz NIE jest wysylany do klienta. Popraw zrodlo w FM1."
					: "k.bloz07 (wysylany do klienta) rozni sie od t.bloz07 z kartoteki (po nim liczy sie zakres kontraktu)."
			}
		>
			{towr}
		</span>
	);
}

// Pobranie tekstu jako plik (Blob) — wspoldzielone przez podglady .sql.
function downloadBlob(text: string, filename: string) {
	const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}

// Kolumny nieistotne przy ocenie roznic (klient wymaga wysylki, ale rozjazd
// niemerytoryczny). Twardo: dgakt. Wiersz istotny = ma rozna kolumne poza tym.
const INSIGNIFICANT_DIFF_COLS = new Set(["dgakt"]);
function isMaterialDiff(row: AggregateDiffRow): boolean {
	return (row.diffColumns ?? []).some((c) => !INSIGNIFICANT_DIFF_COLS.has(c.toLowerCase()));
}

function collectValueKeys(rows: AggregateDiffRow[], kind: DiffKind): string[] {
	const set = new Set<string>();
	for (const r of rows) {
		// 'extra' ma PUSTE sourceValues z definicji — tych wierszy u nas nie ma.
		// Kolumny bierzemy wtedy od klienta, inaczej tabela wyszlaby pusta.
		if (kind === "extra") {
			for (const k of Object.keys(r.destValues ?? {})) set.add(k);
			continue;
		}
		for (const k of Object.keys(r.sourceValues ?? {})) set.add(k);
		if (kind === "different" && r.destValues) {
			for (const k of Object.keys(r.destValues)) set.add(k);
		}
	}
	return [...set];
}

async function fetchAllDiffs(
	connectionId: number,
	queryId: number,
	contractId: number,
	kind: DiffKind,
): Promise<AggregateDiffRow[]> {
	const all: AggregateDiffRow[] = [];
	const pageSize = 5000;
	for (let offset = 0; ; offset += pageSize) {
		const page = await outboundApi.listSfyAggregateDiffs(connectionId, queryId, contractId, {
			kind,
			limit: pageSize,
			offset,
		});
		all.push(...page);
		if (page.length < pageSize) break;
	}
	return all;
}
